/* SpawnAnything — buildings ("Gebäude"):
   recipe schema + validator (LLM-safe), procedural templates per type and upgrade level (1..3),
   gameplay data (hardpoint slots, colliders, spawn point, stats), hardpoint turret models and the preset library.

   COORDINATES (building-local, metres)
   - Origin = centre of the footprint on the ground. x = right, y = up, z = FRONT (doors, gates and spawn face +z).
   - Voxel (i, j, k) covers [i*vs, (i+1)*vs] etc., so meshing with ctr = { cx: 0, cz: 0, y0: 0 } yields metres directly.
   - The footprint [w, d] is the fixed building plot (the size at level 3). Level 1 occupies part of it, upgrades grow
     inside it (castle: keep -> walls + corner towers -> gatehouse, keep roof and side towers).
   - Recipe parts: at[0], at[2] and size[0], size[2] are normalised to the footprint half extents (x = -1 left edge,
     x = +1 right edge, z = +1 front edge); at[1] (centre height) and size[1] (half height) are in metres.
     size is a HALF size (like unit recipes): size [1, 2, 1] = box covering the whole plot, 4 m tall.
   - Slot yaw / turret yaw: radians around +y, 0 = facing +z, yaw = atan2(dirX, dirZ).
   Deterministic: no Math.random anywhere (colour noise uses hash3). */
import { DARK, GOLD, KEY, OLIVE, OLIVE_D, OLIVE_L, ROLE, STEEL, STEEL_D, STEEL_L, T, T2, TL, TYPES, VoxModel, WEAPONS, addOutline, hash3, meshAO, meshGreedy } from './voxel.js';
import { ERA_TYPES, ERA_WEAPONS } from '../content/eras.js';

/* ---------------- constants ---------------- */
export const BUILDING_TYPES = ['fortress', 'tower', 'wall', 'gate', 'barracks', 'house', 'farm', 'workshop', 'bunker', 'reactor', 'silo', 'turret_base', 'dome', 'hut', 'palisade', 'temple'];
export const BUILDING_ERAS = ['urzeit', 'antike', 'mittelalter', 'weltkrieg', 'moderne', 'zukunft'];
export const PART_SHAPES = ['box', 'ellipsoid', 'cylinder', 'cone'];
export const SLOT_KINDS = ['turret', 'catapult', 'archer', 'mg', 'laser'];
export const PALETTE_KEYS = ['wall', 'roof', 'trim', 'accent'];
export const MAX_PARTS = 60;
export const MAX_BUILDING_VOXELS = 60000;
export const DEFAULT_VS = 0.25;
export const MAX_LEVEL = 3;
export const LIMITS = { footprint: [2, 32], atXZ: 1.5, atY: [0, 30], sizeXZ: [0.01, 1.5], sizeY: [0.02, 16], rot: 180, name: 40 };
const WORK_BUDGET = 1.5e6; // max voxel-cells the recipe parts may scan; larger recipes get a coarser voxel size

const FAMILY = { urzeit: 'primitive', antike: 'classic', mittelalter: 'classic', weltkrieg: 'industrial', moderne: 'industrial', zukunft: 'future' };
const ERA_PALETTE = {
  urzeit: { wall: '#8a5a33', roof: '#d6b25a', trim: '#5a3d22', accent: '#eadfc4' },
  antike: { wall: '#e6dcc2', roof: '#b8553a', trim: '#d9b23c', accent: '#2a4fb0' },
  mittelalter: { wall: '#a3a39b', roof: '#5a6478', trim: '#6b4426', accent: '#d9b23c' },
  weltkrieg: { wall: '#a19f8c', roof: '#6b6f5a', trim: '#5f6b3a', accent: '#c8b878' },
  moderne: { wall: '#c9cdd2', roof: '#5a616b', trim: '#3a3f47', accent: '#f2c230' },
  zukunft: { wall: '#e8ecf2', roof: '#3a3f5a', trim: '#5a616b', accent: '#3af0ff' },
};
const ERA_MAT = {
  urzeit: { wall: 'log', roof: 'thatch' }, antike: { wall: 'stone', roof: 'tile' }, mittelalter: { wall: 'stone', roof: 'tile' },
  weltkrieg: { wall: 'concrete', roof: 'concrete' }, moderne: { wall: 'concrete', roof: 'metal' }, zukunft: { wall: 'metal', roof: 'metal' },
};
const WOOD = 0x8a5a33, WOOD_D = 0x5a3d22, WOOD_L = 0xa8743f, STONE = 0x8a8f96, STONE_D = 0x6f747a, GLASS = 0x34404e, IRON = 0x3a3d42;
const SAND = 0xc8b07a, SOIL = 0x6b4a2b, LEAF = 0x5e8c3a, WHEAT = 0xd9b23c, WHITE = 0xf2f0ea, RED = 0xc8322b, CYAN = 0x3af0ff;

/* ---------------- gameplay tables ---------------- */
// hp at level 1, armor = damage reduction 0..1, cap = troop limit bonus per level, cost = build price, up = [to level 2, to level 3]
const TYPE_INFO = {
  fortress: { name: 'Festung', fp: [24, 24], hp: 6000, armor: 0.6, cap: [10, 15, 20], cost: 900, up: [700, 1200], trainer: 'fortress' },
  tower: { name: 'Turm', fp: [6, 6], hp: 1800, armor: 0.5, cost: 300, up: [250, 400], weapon: 'tower' },
  wall: { name: 'Mauer', fp: [12, 3], hp: 2500, armor: 0.7, cost: 150, up: [120, 200] },
  gate: { name: 'Tor', fp: [12, 6], hp: 3000, armor: 0.7, cost: 250, up: [200, 350] },
  barracks: { name: 'Kaserne', fp: [16, 10], hp: 2200, armor: 0.3, cap: [5, 5, 10], cost: 350, up: [300, 500], trainer: 'barracks' },
  house: { name: 'Haus', fp: [8, 8], hp: 900, armor: 0.1, cap: [10, 15, 20], cost: 120, up: [100, 180] },
  farm: { name: 'Bauernhof', fp: [18, 16], hp: 800, armor: 0, cap: [5, 10, 15], income: [2, 4, 6], cost: 150, up: [120, 200] },
  workshop: { name: 'Werkstatt', fp: [12, 10], hp: 1500, armor: 0.25, cost: 300, up: [250, 400], trainer: 'workshop', income: [1, 2, 3] },
  bunker: { name: 'Bunker', fp: [8, 8], hp: 3500, armor: 0.8, cost: 280, up: [250, 400], weapon: 'bunker' },
  reactor: { name: 'Reaktor', fp: [22, 18], hp: 3000, armor: 0.5, income: [6, 10, 16], cost: 900, up: [700, 1100] },
  silo: { name: 'Raketensilo', fp: [12, 12], hp: 2600, armor: 0.6, cost: 1000, up: [800, 1400], weapon: 'silo' },
  turret_base: { name: 'Geschützstellung', fp: [5, 5], hp: 1500, armor: 0.6, cost: 200, up: [150, 300] },
  dome: { name: 'Kuppel', fp: [16, 16], hp: 3500, armor: 0.4, cap: [10, 15, 25], cost: 700, up: [600, 900], trainer: 'dome' },
  hut: { name: 'Hütte', fp: [8, 8], hp: 600, armor: 0.05, cap: [5, 8, 12], cost: 80, up: [60, 120], trainer: 'hut' },
  palisade: { name: 'Palisade', fp: [12, 2], hp: 1200, armor: 0.4, cost: 60, up: [60, 120] },
  temple: { name: 'Tempel', fp: [12, 18], hp: 2500, armor: 0.4, cap: [5, 10, 15], cost: 400, up: [350, 600], trainer: 'temple' },
};
// era rosters (keys of TYPES or ERA_TYPES): basic infantry, elite, heavy
const ROSTER = {
  urzeit: { b: ['hoehlenmensch', 'affe'], e: ['schamane', 'saebelzahntiger'], h: ['mammut'] },
  antike: { b: ['hoplit', 'bogenschuetze'], e: ['legionaer', 'gladiator'], h: ['streitwagen', 'kampfelefant'] },
  mittelalter: { b: ['hellebardier', 'armbrustschuetze'], e: ['ritter', 'musketier'], h: ['lanzenreiter'] },
  weltkrieg: { b: ['ww_soldat', 'sanitaeter'], e: ['mg_schuetze', 'flammenwerfer_soldat'], h: ['panzer'] },
  moderne: { b: ['spezialkraft', 'scharfschuetze'], e: ['raketenschuetze', 'schutzanzug'], h: ['panzer'] },
  zukunft: { b: ['astronaut', 'roboter'], e: ['cyborg', 'alien'], h: ['mech_pilot', 'robohund'] },
};
const TRAINER = {
  fortress: r => [r.b, [...r.b, ...r.e], [...r.b, ...r.e, ...r.h]],
  barracks: r => [r.b, [...r.b, ...r.e], [...r.b, ...r.e]],
  temple: r => [r.b.slice(0, 1), r.b, [...r.b, ...r.e]],
  hut: r => [r.b.slice(0, 1), r.b, [...r.b, r.e[0]]],
  workshop: r => [r.h.slice(0, 1), r.h, r.h],
  dome: r => [r.b, [...r.b, ...r.e], [...r.b, ...r.e, ...r.h]],
};
const TOWER_WEAPON = { urzeit: 'steinwurf', antike: 'bogen', mittelalter: 'armbrust', weltkrieg: 'kanone', moderne: 'raketen', zukunft: 'lasergewehr' };
const BUNKER_WEAPON = { urzeit: 'steinwurf', antike: 'bogen', mittelalter: 'armbrust', weltkrieg: 'mg', moderne: 'minigun', zukunft: 'lasergewehr' };
const ALL_WEAPONS = { ...WEAPONS, ...ERA_WEAPONS };
const HEAVY = { primitive: 'catapult', classic: 'catapult', industrial: 'turret', future: 'laser' };
const LIGHT = { primitive: 'archer', classic: 'archer', industrial: 'mg', future: 'laser' };

/* ---------------- small helpers ---------------- */
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const r3 = v => Math.round(v * 1000) / 1000;
const isObj = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const get = (o, k) => { try { return o == null ? undefined : o[k]; } catch { return undefined; } };
function toNum(v, d) {
  const n = typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' && v.length < 40) ? Number(v) : NaN;
  return Number.isFinite(n) ? n : d;
}
function shade(c, f) {
  if (c < 0) return f < 0.93 ? (c === T ? T2 : c) : f > 1.07 ? (c === T ? TL : c) : c;
  const r = clamp(((c >> 16) & 255) * f, 0, 255) | 0, g = clamp(((c >> 8) & 255) * f, 0, 255) | 0, b = clamp((c & 255) * f, 0, 255) | 0;
  return (r << 16) | (g << 8) | b;
}
const hexInt = s => (s === 'team' ? T : parseInt(s.slice(1), 16));

/* materials: base colour -> colour function of the voxel index (comic texture, deterministic) */
const MATS = {
  flat: c => c,
  stone: c => (i, j, k) => { const row = Math.floor(j / 2), b = Math.floor((i + k + (row & 1) * 2) / 4); return shade(c, 0.88 + hash3(b, row, 11) * 0.18); },
  brick: c => (i, j, k) => { const b = Math.floor((i + k + (j & 1)) / 2); return shade(c, (j % 4 === 0 ? 0.84 : 0.94) + hash3(b, j, 5) * 0.12); },
  log: c => (i, j, k) => shade(c, ((j >> 1) & 1 ? 0.86 : 1.04) + (hash3(i, j >> 1, k) - 0.5) * 0.08),
  plank: c => (i, j, k) => shade(c, 0.86 + hash3((i + k) >> 1, 3, 5) * 0.22),
  thatch: c => (i, j, k) => shade(c, (j & 1 ? 0.9 : 1.04) + (hash3(i, j, k) - 0.5) * 0.12),
  tile: c => (i, j, k) => shade(c, (j & 1 ? 0.86 : 1.02) + (hash3(i >> 1, j, k >> 1) - 0.5) * 0.06),
  plaster: c => (i, j, k) => shade(c, 0.97 + hash3(i, j, k) * 0.06),
  concrete: c => (i, j, k) => shade(c, ((i + k) % 10 === 0 || j % 8 === 0) ? 0.86 : 0.95 + hash3(i, j, k) * 0.08),
  metal: c => (i, j, k) => shade(c, ((i + k) % 8 === 0 || j % 6 === 0) ? 0.82 : 1),
  corrugated: c => (i, j, k) => shade(c, (i & 1) ? 0.84 : 1.02),
  sandbag: c => (i, j, k) => { const row = Math.floor(j / 2), b = Math.floor((i + k + (row & 1) * 2) / 3); return shade(c, (j & 1) && (i + k + (row & 1) * 2) % 3 === 0 ? 0.78 : 0.9 + hash3(b, row, 2) * 0.16); },
};

/* inside tests in normalised local coordinates (u, v, w) in [-1, 1] */
const INSIDE = {
  box: (u, v, w) => u >= -1 && u <= 1 && v >= -1 && v <= 1 && w >= -1 && w <= 1,
  ellipsoid: (u, v, w) => u * u + v * v + w * w <= 1,
  dome: (u, v, w) => v >= 0 && u * u + v * v + w * w <= 1,
  cylinder: (u, v, w) => v >= -1 && v <= 1 && u * u + w * w <= 1,
  cylx: (u, v, w) => u >= -1 && u <= 1 && v * v + w * w <= 1,
  cylz: (u, v, w) => w >= -1 && w <= 1 && u * u + v * v <= 1,
  cone: (u, v, w) => { if (v < -1 || v > 1) return false; const rf = 0.06 + (1 - v) * 0.47; return u * u + w * w <= rf * rf; },
  pyramid: (u, v, w) => { if (v < -1 || v > 1) return false; const rf = 0.04 + (1 - v) * 0.48; return Math.abs(u) <= rf && Math.abs(w) <= rf; },
  gablex: (u, v, w) => u >= -1 && u <= 1 && v >= -1 && v <= 1 && Math.abs(w) <= 0.03 + (1 - v) * 0.485,
  gablez: (u, v, w) => w >= -1 && w <= 1 && v >= -1 && v <= 1 && Math.abs(u) <= 0.03 + (1 - v) * 0.485,
};
function eulerInv(rot, mirror) {
  const d = Math.PI / 180, a = rot[0] * d, b = (mirror ? -rot[1] : rot[1]) * d, c = (mirror ? -rot[2] : rot[2]) * d;
  if (!a && !b && !c) return null;
  const ca = Math.cos(a), sa = Math.sin(a), cb = Math.cos(b), sb = Math.sin(b), cc = Math.cos(c), sc = Math.sin(c);
  // R = Rx * Ry * Rz (three.js 'XYZ'); inverse = transpose
  const R = [
    [cb * cc, -cb * sc, sb],
    [sa * sb * cc + ca * sc, -sa * sb * sc + ca * cc, -sa * cb],
    [-ca * sb * cc + sa * sc, ca * sb * sc + sa * cc, ca * cb],
  ];
  return [R[0][0], R[1][0], R[2][0], R[0][1], R[1][1], R[2][1], R[0][2], R[1][2], R[2][2]];
}

/* ---------------- Site: metre-based voxel drawing + gameplay bookkeeping ---------------- */
class Site {
  constructor(R, level, vs) {
    this.R = R; this.L = level; this.vs = vs; this.type = R.type; this.era = R.era; this.fam = FAMILY[R.era];
    this.W = R.footprint[0]; this.D = R.footprint[1]; this.hw = this.W / 2; this.hd = this.D / 2;
    this.m = new VoxModel(vs); this.m.kind = 'building';
    this.g = 0; this.ng = 0; this.part = 0; this.e = 0;
    const pal = {}; for (const k of PALETTE_KEYS) pal[k] = hexInt(R.palette[k]); this.pal = pal;
    this.heavy = HEAVY[this.fam]; this.light = LIGHT[this.fam];
    this.slots = []; this.colliders = []; this.spawn = [0, this.hd + 1.5]; this.turretFrom = null;
  }
  mat(role, kind) {
    const c = this.pal[role];
    return MATS[kind || (role === 'wall' || role === 'roof' ? ERA_MAT[this.era][role] : 'flat')](c);
  }
  grp() { this.g = ++this.ng; return this; }
  glow(on) { this.e = on ? 1 : 0; return this; }
  put(i, j, k, c) {
    if (j < 0 || j > 1000 || i < -500 || i > 500 || k < -500 || k > 500) return;
    this.m.map.set(KEY(i, j, k), { c: typeof c === 'function' ? c(i, j, k) : c, p: this.part, e: this.e, g: this.g });
  }
  rng(a0, a1) {
    const vs = this.vs; let i0 = Math.ceil(a0 / vs - 0.5 - 1e-7), i1 = Math.floor(a1 / vs - 0.5 + 1e-7);
    if (i0 > i1) i0 = i1 = Math.round((a0 + a1) / (2 * vs) - 0.5);
    return [i0, i1];
  }
  /* axis-aligned box (metres); hollow shell with an open bottom on the ground unless solid */
  box(x0, y0, z0, x1, y1, z1, c, solid = false) {
    const [i0, i1] = this.rng(Math.min(x0, x1), Math.max(x0, x1)), [k0, k1] = this.rng(Math.min(z0, z1), Math.max(z0, z1));
    let [j0, j1] = this.rng(Math.min(y0, y1), Math.max(y0, y1)); if (j0 < 0) j0 = 0; if (j1 < j0) return this;
    for (let i = i0; i <= i1; i++) for (let k = k0; k <= k1; k++) {
      if (solid || i === i0 || i === i1 || k === k0 || k === k1) for (let j = j0; j <= j1; j++) this.put(i, j, k, c);
      else { if (j0 > 0) this.put(i, j0, k, c); this.put(i, j1, k, c); }
    }
    return this;
  }
  /* generic convex shape (centre + half extents, metres) rasterised as a watertight 1-voxel shell */
  shape(kind, cx, cy, cz, hx, hy, hz, c, inv = null) {
    const vs = this.vs, mn = vs * 0.55, inside = INSIDE[kind] || INSIDE.box;
    hx = Math.max(hx, mn); hy = Math.max(hy, mn); hz = Math.max(hz, mn);
    let ex = hx, ey = hy, ez = hz;
    if (inv) ex = ey = ez = Math.hypot(hx, hy, hz);
    const [i0, i1] = this.rng(cx - ex, cx + ex), [k0, k1] = this.rng(cz - ez, cz + ez);
    let [j0, j1] = this.rng(kind === 'dome' && !inv ? cy : cy - ey, cy + ey); if (j0 < 0) j0 = 0; if (j1 < j0) return this;
    const nx = i1 - i0 + 1, nz = k1 - k0 + 1, lo = new Int32Array(nx * nz).fill(1 << 29), hi = new Int32Array(nx * nz).fill(-(1 << 29));
    for (let a = 0; a < nx; a++) {
      const qx = (i0 + a + 0.5) * vs - cx;
      for (let b = 0; b < nz; b++) {
        const qz = (k0 + b + 0.5) * vs - cz, id = a * nz + b;
        for (let j = j0; j <= j1; j++) {
          const qy = (j + 0.5) * vs - cy;
          let u = qx, v = qy, w = qz;
          if (inv) { u = inv[0] * qx + inv[3] * qy + inv[6] * qz; v = inv[1] * qx + inv[4] * qy + inv[7] * qz; w = inv[2] * qx + inv[5] * qy + inv[8] * qz; }
          if (inside(u / hx, v / hy, w / hz)) { if (j < lo[id]) lo[id] = j; hi[id] = j; }
        }
      }
    }
    for (let a = 0; a < nx; a++) for (let b = 0; b < nz; b++) {
      const id = a * nz + b, l = lo[id], h = hi[id]; if (l > h) continue;
      const n0 = a > 0 ? id - nz : -1, n1 = a < nx - 1 ? id + nz : -1, n2 = b > 0 ? id - 1 : -1, n3 = b < nz - 1 ? id + 1 : -1;
      for (let j = l; j <= h; j++) {
        let sh = j === h || (j === l && j > 0);
        if (!sh) for (const n of [n0, n1, n2, n3]) if (n < 0 || lo[n] > j || hi[n] < j) { sh = true; break; }
        if (sh) this.put(i0 + a, j, k0 + b, c);
      }
    }
    return this;
  }
  cyl(cx, cz, r, y0, y1, c) { return this.shape('cylinder', cx, (y0 + y1) / 2, cz, r, (y1 - y0) / 2, r, c); }
  cone(cx, cz, r, y0, y1, c) { return this.shape('cone', cx, (y0 + y1) / 2, cz, r, (y1 - y0) / 2, r, c); }
  /* open tube (cooling towers): radius as a function of height */
  tube(cx, cz, rFn, y0, y1, thick, c) {
    const vs = this.vs, [j0, j1] = this.rng(y0, y1);
    for (let j = Math.max(0, j0); j <= j1; j++) {
      const r = rFn((j + 0.5) * vs), [i0, i1] = this.rng(cx - r, cx + r), [k0, k1] = this.rng(cz - r, cz + r);
      for (let i = i0; i <= i1; i++) for (let k = k0; k <= k1; k++) {
        const d = Math.hypot((i + 0.5) * vs - cx, (k + 0.5) * vs - cz);
        if (d <= r && d > r - thick) this.put(i, j, k, c);
      }
    }
    return this;
  }
  /* recolour existing voxels (optionally only where pred(i,j,k)) */
  paint(x0, y0, z0, x1, y1, z1, c, pred = null) {
    const [i0, i1] = this.rng(x0, x1), [j0, j1] = this.rng(y0, y1), [k0, k1] = this.rng(z0, z1);
    for (let i = i0; i <= i1; i++) for (let j = Math.max(0, j0); j <= j1; j++) for (let k = k0; k <= k1; k++) {
      const v = this.m.map.get(KEY(i, j, k));
      if (v && (!pred || pred(i, j, k))) { v.c = typeof c === 'function' ? c(i, j, k) : c; v.e = this.e; }
    }
    return this;
  }
  /* 3D line of single voxels */
  line(x0, y0, z0, x1, y1, z1, c) {
    const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0, z1 - z0) / (this.vs * 0.5)));
    for (let t = 0; t <= n; t++) {
      const f = t / n, x = x0 + (x1 - x0) * f, y = y0 + (y1 - y0) * f, z = z0 + (z1 - z0) * f;
      this.put(Math.floor(x / this.vs), Math.floor(y / this.vs), Math.floor(z / this.vs), c);
    }
    return this;
  }
  /* 4 boxes forming a band around a rect: from (edge - out) to (edge + inn) */
  ring(x0, z0, x1, z1, y0, y1, out, inn, c) {
    this.box(x0 - out, y0, z0 - out, x1 + out, y1, z0 + inn, c);
    this.box(x0 - out, y0, z1 - inn, x1 + out, y1, z1 + out, c);
    this.box(x0 - out, y0, z0, x0 + inn, y1, z1, c);
    this.box(x1 - inn, y0, z0, x1 + out, y1, z1, c);
    return this;
  }
  /* box on a face: (cx, cz) = point on the face plane, dir 0:+z 1:+x 2:-z 3:-x, a = along the face, n = outward */
  face(cx, cz, dir, a0, a1, y0, y1, c, n0 = -0.6 * this.vs, n1 = this.vs) {
    const nx = [0, 1, 0, -1][dir], nz = [1, 0, -1, 0][dir], tx = nz, tz = -nx;
    const xa = cx + a0 * tx + n0 * nx, za = cz + a0 * tz + n0 * nz, xb = cx + a1 * tx + n1 * nx, zb = cz + a1 * tz + n1 * nz;
    return this.box(Math.min(xa, xb), y0, Math.min(za, zb), Math.max(xa, xb), y1, Math.max(za, zb), c, true);
  }
  door(cx, cz, dir, w, h) {
    const fut = this.fam === 'future';
    if (fut) this.glow(1);
    this.face(cx, cz, dir, -w / 2 - 0.25, w / 2 + 0.25, 0, h + 0.25, fut ? this.pal.accent : shade(this.pal.trim, 0.85));
    this.glow(0);
    this.face(cx, cz, dir, -w / 2, w / 2, 0, h, fut ? 0x2a2f3a : (i, j, k) => ((i + k) & 1 ? WOOD_D : 0x4a3220));
    return this;
  }
  window(cx, cz, dir, a, y, w = 0.75, h = 1) {
    const fut = this.fam === 'future';
    this.face(cx, cz, dir, a - w / 2 - 0.25, a + w / 2 + 0.25, y - 0.25, y + h + 0.25, fut ? this.pal.trim : this.pal.trim);
    if (fut) this.glow(1);
    this.face(cx, cz, dir, a - w / 2, a + w / 2, y, y + h, fut ? shade(this.pal.accent, 0.8) : GLASS);
    return this.glow(0);
  }
  flag(x, y, z, h = 2.4) {
    this.box(x - 0.05, y, z - 0.05, x + 0.05, y + h, z + 0.05, DARK);
    this.box(x - 0.1, y + h + 0.05, z - 0.1, x + 0.1, y + h + 0.3, z + 0.1, GOLD, true);
    this.box(x + 0.15, y + h - 0.9, z - 0.05, x + 1.4, y + h - 0.1, z + 0.05, (i, j) => (j & 1 ? T : T2), true);
    return this;
  }
  banner(cx, cz, dir, a, yTop, w, h) {
    this.face(cx, cz, dir, a - w / 2 - 0.15, a + w / 2 + 0.15, yTop - 0.25, yTop, this.pal.trim);
    this.face(cx, cz, dir, a - w / 2, a + w / 2, yTop - h, yTop - 0.25, (i, j) => (j % 4 === 0 ? T2 : T));
    this.face(cx, cz, dir, a - 0.25, a + 0.25, yTop - h * 0.55, yTop - h * 0.3, this.pal.accent, 0.4 * this.vs, 1.4 * this.vs);
    return this;
  }
  /* battlements / parapet on top of a rect (family aware) */
  cren(x0, z0, x1, z1, y, h = 0.75) {
    const f = this.fam, vs = this.vs, t = Math.max(vs, Math.min(0.5, (x1 - x0) / 3, (z1 - z0) / 3));
    if (f === 'industrial') return this.ring(x0, z0, x1, z1, y, y + 0.55, 0, 0.25, MATS.concrete(shade(this.pal.wall, 0.92)));
    if (f === 'future') { this.ring(x0, z0, x1, z1, y, y + 0.4, 0, 0.25, this.pal.trim); return this.glow(1).ring(x0, z0, x1, z1, y + 0.4, y + 0.5, 0, 0.25, this.pal.accent).glow(0); }
    const [i0, i1] = this.rng(x0, x1), [k0, k1] = this.rng(z0, z1), ti = Math.max(1, Math.round(t / vs));
    const [ja, jb] = this.rng(y, y + h), c = f === 'primitive' ? MATS.log(this.pal.wall) : this.mat('wall');
    for (let i = i0; i <= i1; i++) for (let k = k0; k <= k1; k++) {
      const ex = i < i0 + ti || i > i1 - ti, ez = k < k0 + ti || k > k1 - ti;
      if (!ex && !ez) continue;
      const along = ez ? i : k;
      if (f === 'primitive') { const top = jb + ((along & 1) ? -1 : 0) + (along % 3 === 0 ? 1 : 0); for (let j = ja; j <= top; j++) this.put(i, j, k, c); continue; }
      if (!(ex && ez) && (Math.floor((along * vs) / 0.75 + 1000) & 1)) continue;
      for (let j = ja; j <= jb; j++) this.put(i, j, k, c);
    }
    return this;
  }
  crenRound(cx, cz, r, y, h = 0.75, c = this.mat('wall')) {
    const vs = this.vs, [i0, i1] = this.rng(cx - r, cx + r), [k0, k1] = this.rng(cz - r, cz + r), [ja, jb] = this.rng(y, y + h);
    for (let i = i0; i <= i1; i++) for (let k = k0; k <= k1; k++) {
      const dx = (i + 0.5) * vs - cx, dz = (k + 0.5) * vs - cz, d = Math.hypot(dx, dz);
      if (d > r || d < r - 0.5) continue;
      if (Math.floor(((Math.atan2(dz, dx) + Math.PI) * r) / 0.75) & 1) continue;
      for (let j = ja; j <= jb; j++) this.put(i, j, k, c);
    }
    return this;
  }
  /* gable roof: wall-coloured gable ends + sloped roof slabs with overhang; ridge along 'x' or 'z' */
  roofGable(x0, x1, z0, z1, y0, rh, axis, c, endC, ov = 0.35) {
    const vs = this.vs, cx = (x0 + x1) / 2, cz = (z0 + z1) / 2, hx = (x1 - x0) / 2, hz = (z1 - z0) / 2;
    const half = axis === 'x' ? hz : hx, slope = rh / half, th = Math.max(vs * 1.5, slope * vs + vs * 0.75);
    const eh = Math.max(vs, rh - th * 0.8); // gable ends stay below the roof slab
    if (endC != null) this.shape(axis === 'x' ? 'gablex' : 'gablez', cx, y0 + eh / 2, cz, hx, eh / 2, hz, endC);
    const [i0, i1] = this.rng(x0 - ov, x1 + ov), [k0, k1] = this.rng(z0 - ov, z1 + ov);
    for (let i = i0; i <= i1; i++) for (let k = k0; k <= k1; k++) {
      const off = axis === 'x' ? Math.abs((k + 0.5) * vs - cz) : Math.abs((i + 0.5) * vs - cx);
      const yt = y0 + rh * (1 - off / half), [ja, jb] = this.rng(yt - th, yt);
      for (let j = Math.max(0, ja); j <= jb; j++) this.put(i, j, k, c);
    }
    if (axis === 'x') this.box(x0 - ov, y0 + rh - 0.05, cz - 0.15, x1 + ov, y0 + rh + 0.2, cz + 0.15, this.pal.trim, true);
    else this.box(cx - 0.15, y0 + rh - 0.05, z0 - ov, cx + 0.15, y0 + rh + 0.2, z1 + ov, this.pal.trim, true);
    return this;
  }
  /* timber framing (Fachwerk) on the front/back faces */
  timber(x0, z0, x1, z1, h, mids = []) {
    const t = this.pal.trim, w = x1 - x0, n = Math.max(2, Math.round(w / 1.4)), cx = (x0 + x1) / 2;
    for (const [dir, zf] of [[0, z1], [2, z0]]) {
      const sg = dir === 0 ? 1 : -1;
      for (let p = 0; p <= n; p++) { const a = (-w / 2 + (w * p) / n) * sg; this.face(cx, zf, dir, a - 0.12, a + 0.12, 0, h, t); }
      for (const y of [0, h - 0.25, ...mids]) this.face(cx, zf, dir, -w / 2, w / 2, y, y + 0.25, t);
      const rows = [0, ...mids, h];
      for (let p = 0; p < n; p += 2) for (let r = 0; r < rows.length - 1; r++) {
        const aA = -w / 2 + (w * p) / n, aB = -w / 2 + (w * (p + 1)) / n, ya = rows[r], yb = rows[r + 1];
        const xa = cx + aA, xb = cx + aB, z = zf + sg * this.vs * 0.5;
        this.line(xa, ya, z, xb, yb, z, t);
      }
    }
    for (const xf of [x0, x1]) for (const z of [z0 + 0.12, z1 - 0.12]) this.box(xf - 0.12, 0, z - 0.12, xf + 0.12, h, z + 0.12, t, true);
    return this;
  }
  slot(kind, x, y, z, yaw = 0) {
    if (y > 0) y = (Math.floor(y / this.vs - 0.5 + 1e-7) + 1) * this.vs; // snap to the voxel surface
    this.slots.push({ kind, pos: [r3(x), r3(y), r3(z)], yaw: r3(yaw) }); return this; }
  col(x0, z0, x1, z1, passTeam = false) {
    const a0 = clamp(Math.min(x0, x1), -this.hw, this.hw), a1 = clamp(Math.max(x0, x1), -this.hw, this.hw);
    const b0 = clamp(Math.min(z0, z1), -this.hd, this.hd), b1 = clamp(Math.max(z0, z1), -this.hd, this.hd);
    if (a1 - a0 < 0.05 || b1 - b0 < 0.05) return this;
    this.colliders.push({ x: r3((a0 + a1) / 2), z: r3((b0 + b1) / 2), w: r3(a1 - a0), d: r3(b1 - b0), passTeam: !!passTeam });
    return this;
  }

  /* ---------- composite pieces (family aware) ---------- */
  logs(x0, z0, x1, z1, h) {
    const c = MATS.plank(this.pal.wall), vs = this.vs;
    this.box(x0, 0, z0, x1, h, z1, c);
    const [i0, i1] = this.rng(x0, x1), [k0, k1] = this.rng(z0, z1), jt = this.rng(h, h)[1];
    const alongX = x1 - x0 >= z1 - z0;
    for (let i = i0; i <= i1; i++) for (let k = k0; k <= k1; k++) {
      const a = alongX ? i : k, edge = alongX ? (k === k0 || k === k1) : (i === i0 || i === i1);
      if (!edge && k1 - k0 > 2 && i1 - i0 > 2) continue;
      if ((a & 1) === 0) { this.put(i, jt + 1, k, c); if (a % 4 === 0) this.put(i, jt + 2, k, shade(this.pal.wall, 1.1)); }
    }
    return this;
  }
  sandbags(x0, z0, x1, z1, h) { return this.box(x0, 0, z0, x1, h, z1, MATS.sandbag(this.fam === 'industrial' && this.era === 'moderne' ? 0xb8a77a : SAND)); }
  wallSeg(x0, z0, x1, z1, h) {
    if (x1 - x0 < 0.3 || z1 - z0 < 0.3) return this;
    this.grp();
    const f = this.fam;
    if (f === 'primitive') this.logs(x0, z0, x1, z1, h);
    else {
      this.box(x0, 0, z0, x1, h, z1, this.mat('wall'));
      if (f === 'future') this.glow(1).ring(x0, z0, x1, z1, h * 0.5, h * 0.5 + 0.2, this.vs, this.vs * 0.5, this.pal.accent).glow(0);
      if (f === 'classic') this.ring(x0, z0, x1, z1, 0, 0.5, 0.25, 0, shade(this.pal.wall, 0.82));
      this.cren(x0, z0, x1, z1, h);
    }
    return this.col(x0, z0, x1, z1);
  }
  /* tower of the building's family at (cx, cz), radius/half size r, body height h; returns the platform height */
  towerAt(cx, cz, r, h, o = {}) {
    this.grp();
    const f = this.fam, wallM = this.mat('wall'), vs = this.vs;
    this.col(cx - r, cz - r, cx + r, cz + r);
    if (f === 'primitive') {
      const p = r - 0.25, post = MATS.log(this.pal.wall);
      for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        this.box(cx + sx * p - 0.2, 0, cz + sz * p - 0.2, cx + sx * p + 0.2, h + (o.roof ? 2.1 : 0.9), cz + sz * p + 0.2, post, true);
      }
      for (let y = 1.2; y < h - 0.6; y += 1.8) {
        this.box(cx - p, y, cz - p - 0.1, cx + p, y + 0.2, cz - p + 0.1, post, true).box(cx - p, y, cz + p - 0.1, cx + p, y + 0.2, cz + p + 0.1, post, true);
        this.line(cx - p, y, cz + p + 0.15, cx + p, y + 1.6, cz + p + 0.15, this.pal.trim);
      }
      this.box(cx - r - 0.2, h - 0.35, cz - r - 0.2, cx + r + 0.2, h, cz + r + 0.2, MATS.plank(this.pal.trim));
      this.ring(cx - r - 0.2, cz - r - 0.2, cx + r + 0.2, cz + r + 0.2, h + 0.7, h + 0.9, 0, 0.2, this.pal.trim);
      if (o.roof) { this.cone(cx, cz, r + 0.7, h + 2.0, h + 2.0 + r * 1.6, this.mat('roof', 'thatch')); this.flag(cx, h + 1.9 + r * 1.6, cz, 1.2); }
      return h;
    }
    if (f === 'industrial') {
      this.box(cx - r, 0, cz - r, cx + r, h, cz + r, wallM);
      this.ring(cx - r, cz - r, cx + r, cz + r, h - 0.6, h, 0.2, 0, MATS.concrete(shade(this.pal.wall, 0.88)));
      this.cren(cx - r - 0.2, cz - r - 0.2, cx + r + 0.2, cz + r + 0.2, h);
      for (let d = 0; d < 4; d++) this.face(cx + [0, r, 0, -r][d], cz + [r, 0, -r, 0][d], d, -r * 0.6, r * 0.6, h - 1.7, h - 1.4, DARK);
      this.ring(cx - r, cz - r, cx + r, cz + r, h * 0.72, h * 0.72 + 0.4, this.vs, this.vs * 0.5, T);
      if (this.era === 'moderne') this.ring(cx - r, cz - r, cx + r, cz + r, 0.8, 1.3, vs, vs * 0.5, (i, j, k) => (((i + k + j) >> 1) & 1 ? this.pal.accent : DARK));
      if (o.door !== false) this.door(cx, cz + r, 0, 1, 2);
      return h;
    }
    if (f === 'future') {
      this.cyl(cx, cz, r, 0, 0.8, this.mat('wall'));
      this.cyl(cx, cz, r * 0.55, 0.8, h - 0.4, this.mat('wall'));
      this.glow(1); for (let y = 2; y < h - 1; y += 2.5) this.cyl(cx, cz, r * 0.6 + vs, y, y + 0.25, this.pal.accent); this.glow(0);
      this.cyl(cx, cz, r, h - 0.4, h, this.pal.trim);
      this.glow(1).crenRound(cx, cz, r, h, 0.25, this.pal.accent).glow(0);
      return h;
    }
    // classic: round (mittelalter) or square (antike) stone tower
    const sq = o.square ?? this.era === 'antike';
    const rr = o.overhang ? r + 0.4 : r;
    if (sq) {
      this.box(cx - r, 0, cz - r, cx + r, h, cz + r, wallM);
      this.ring(cx - r, cz - r, cx + r, cz + r, 0, 0.6, 0.25, 0, shade(this.pal.wall, 0.82));
      if (o.overhang) this.ring(cx - r, cz - r, cx + r, cz + r, h - 0.7, h, 0.4, 0, shade(this.pal.wall, 0.9));
      if (o.roof) this.shape('pyramid', cx, h + rr * 0.9, cz, rr + 0.3, rr * 0.9, rr + 0.3, this.mat('roof'));
      else this.cren(cx - rr, cz - rr, cx + rr, cz + rr, h);
    } else {
      this.cyl(cx, cz, r, 0, h, wallM);
      this.cyl(cx, cz, r + 0.25, 0, 0.6, shade(this.pal.wall, 0.82));
      if (o.overhang) this.cyl(cx, cz, rr, h - 0.7, h, shade(this.pal.wall, 0.9));
      if (o.roof) this.cone(cx, cz, rr + 0.35, h, h + rr * 2.1, this.mat('roof'));
      else this.crenRound(cx, cz, rr, h);
    }
    if (o.roof) this.flag(cx, h + (sq ? rr * 1.8 : rr * 2.1) - 0.2, cz, 1.6);
    for (const y of [h * 0.4, h * 0.7]) this.face(cx, cz + r, 0, -0.15, 0.15, y, y + 0.8, GLASS, -0.9 * vs, vs * 0.6);
    if (o.door) this.door(cx, cz + r, 0, 1.2, 2.2);
    return h;
  }
  /* gate opening along x centred at (cx, cz): piers, lintel, doors; collider passTeam */
  gateway(cx, cz, w, h, depth, pier = 0.7) {
    this.grp();
    const f = this.fam, hd = depth / 2, wallM = this.mat('wall'), top = h + 1.1;
    this.col(cx - w / 2, cz - hd, cx + w / 2, cz + hd, true);
    if (pier > 0) for (const s of [-1, 1]) {
      const xa = cx + s * w / 2, xb = cx + s * (w / 2 + pier);
      if (f === 'primitive') this.logs(Math.min(xa, xb), cz - hd, Math.max(xa, xb), cz + hd, top);
      else this.box(Math.min(xa, xb), 0, cz - hd, Math.max(xa, xb), f === 'industrial' ? h * 0.7 : top, cz + hd, wallM);
      this.col(Math.min(xa, xb), cz - hd, Math.max(xa, xb), cz + hd);
    }
    if (f === 'industrial') {
      this.box(cx - w / 2, h * 0.35, cz + hd - 0.3, cx + w / 2, h * 0.35 + 0.2, cz + hd - 0.1, (i) => ((i >> 1) & 1 ? RED : WHITE), true);
      return h * 0.7;
    }
    if (f === 'future') {
      this.box(cx - w / 2, h, cz - hd, cx + w / 2, h + 0.5, cz + hd, this.pal.trim);
      this.glow(1).box(cx - w / 2, 0, cz - 0.05, cx + w / 2, h, cz + 0.05, (i, j) => ((i + j) % 3 === 0 ? this.pal.accent : shade(this.pal.accent, 0.7)), true).glow(0);
      return h + 0.5;
    }
    if (f === 'primitive') this.box(cx - w / 2 - 0.2, h, cz - hd, cx + w / 2 + 0.2, h + 0.5, cz + hd, MATS.log(this.pal.trim));
    else {
      this.box(cx - w / 2, h, cz - hd, cx + w / 2, top, cz + hd, wallM);
      this.face(cx, cz + hd, 0, -w / 2 - 0.3, w / 2 + 0.3, h - 0.3, h + 0.3, shade(this.pal.wall, 0.8));
      this.cren(cx - w / 2 - pier, cz - hd, cx + w / 2 + pier, cz + hd, top);
    }
    this.box(cx - w / 2, 0, cz - 0.12, cx + w / 2, h, cz + 0.12, (i, j) => (j % 5 === 2 ? IRON : (i & 1 ? WOOD_D : 0x4a3220)), true);
    if (this.L === 3 && f === 'classic') {
      const [i0, i1] = this.rng(cx - w / 2, cx + w / 2), [j0, j1] = this.rng(h * 0.45, h), k = this.rng(cz + hd - 0.3, cz + hd - 0.3)[0];
      for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) if ((i - i0) % 3 === 0 || (j - j0) % 3 === 0) this.put(i, j, k, IRON);
    }
    return top;
  }
  keep(cx, cz, kx, kz, h) {
    this.grp();
    const L = this.L, wallM = this.mat('wall'), fut = this.fam === 'future';
    this.col(cx - kx, cz - kz, cx + kx, cz + kz);
    this.box(cx - kx, 0, cz - kz, cx + kx, h, cz + kz, wallM);
    this.ring(cx - kx, cz - kz, cx + kx, cz + kz, 0, 0.6, 0.25, 0, shade(this.pal.wall, 0.8));
    this.ring(cx - kx, cz - kz, cx + kx, cz + kz, h * 0.5 - 0.12, h * 0.5 + 0.12, this.vs, 0, fut ? this.pal.accent : shade(this.pal.wall, 0.85));
    const o = L >= 2 ? 0.3 : 0;
    if (o) this.ring(cx - kx, cz - kz, cx + kx, cz + kz, h - 0.6, h, o, 0, shade(this.pal.wall, 0.9));
    this.cren(cx - kx - o, cz - kz - o, cx + kx + o, cz + kz + o, h);
    this.door(cx, cz + kz, 0, 1.8, 2.8);
    for (const y of [h * 0.42, h * 0.68]) {
      for (const a of [-kx * 0.5, kx * 0.5]) { this.window(cx, cz + kz, 0, a, y, 0.5, 0.9); this.window(cx, cz - kz, 2, a, y, 0.5, 0.9); }
      this.window(cx + kx, cz, 1, 0, y, 0.5, 0.9); this.window(cx - kx, cz, 3, 0, y, 0.5, 0.9);
    }
    if (L >= 2) { this.banner(cx, cz + kz, 0, -kx * 0.5, h - 1.0, 1.1, 2.8); this.banner(cx, cz + kz, 0, kx * 0.5, h - 1.0, 1.1, 2.8); }
    const H = this.heavy, Lt = this.light;
    if (L < 3) {
      this.flag(cx + kx - 0.6, h, cz - kz + 0.6, 2.6);
      this.slot(Lt, cx - kx * 0.5, h, cz + kz * 0.5, 0).slot(Lt, cx + kx * 0.5, h, cz + kz * 0.5, 0);
      if (L === 2) this.slot(H, cx - kx * 0.3, h, cz - kz * 0.35, Math.PI);
    } else {
      const ins = Math.min(1.5, kx * 0.4), rh = (kx - ins) * 1.3;
      this.shape('pyramid', cx, h + rh / 2, cz, kx - ins, rh / 2, kz - ins, this.mat('roof'));
      this.flag(cx, h + rh - 0.1, cz, 2.4);
      for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        this.grp();
        const tx = cx + sx * (kx - 0.75), tz = cz + sz * (kz - 0.75);
        this.cyl(tx, tz, 1.0, h - 1.5, h + 2.0, wallM);
        this.cone(tx, tz, 1.3, h + 2.0, h + 4.2, this.mat('roof'));
      }
      this.slot(Lt, cx, h, cz + kz - 0.6, 0).slot(H, cx, h, cz - kz + 0.6, Math.PI);
    }
  }
  turretHead(y) { this.part = this.m.addPart(ROLE.TURRET, y / this.vs, 0); return this; }
}

/* ---------------- templates: one per type, driven by footprint + level ---------------- */
const TPL = {
  fortress(s) {
    const { hw, hd, L } = s;
    const k = Math.min(hw, hd) * 0.34, kcz = L === 1 ? 0 : -hd * 0.14, kh = [8, 10, 12.5][L - 1];
    s.keep(0, kcz, k, k, kh);
    if (L === 1) { s.spawn = [0, r3(k + 1.5)]; return; }
    const tr = L === 2 ? 1.8 : 2.2, wt = 1.2, wh = L === 2 ? 4.5 : 5.5, th = wh + (L === 2 ? 2 : 2.8), gw = 4;
    const x0 = -hw + tr, x1 = hw - tr, z0 = -hd + tr, z1 = hd - tr;
    s.wallSeg(x0, z0 - wt / 2, x1, z0 + wt / 2, wh);
    s.wallSeg(x0 - wt / 2, z0, x0 + wt / 2, z1, wh);
    s.wallSeg(x1 - wt / 2, z0, x1 + wt / 2, z1, wh);
    const gx = L === 3 ? gw / 2 + 2.5 : gw / 2 + 0.7;
    s.wallSeg(x0, z1 - wt / 2, -gx, z1 + wt / 2, wh);
    s.wallSeg(gx, z1 - wt / 2, x1, z1 + wt / 2, wh);
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const cx = sx < 0 ? x0 : x1, cz = sz < 0 ? z0 : z1;
      const top = s.towerAt(cx, cz, tr, th, { overhang: L === 3 });
      s.slot(s.heavy, cx, top, cz, Math.atan2(sx, sz));
    }
    s.slot(s.light, (x0 - gx) / 2, wh, z1, 0).slot(s.light, (gx + x1) / 2, wh, z1, 0);
    if (L === 2) s.gateway(0, z1, gw, wh - 0.6, wt + 0.5, 0.7);
    else {
      s.gateway(0, z1, gw, wh - 0.4, wt + 0.6, 0);
      for (const sx of [-1, 1]) {
        const top = s.towerAt(sx * (gw / 2 + 1.25), z1 + 0.05, 1.25, wh + 2.4, { square: true, roof: true });
        s.banner(sx * (gw / 2 + 1.25), z1 + 1.3, 0, 0, top - 0.6, 1.0, 2.6);
      }
      for (const [cx, cz, yaw, along] of [[x0, (z0 + z1) / 2, -Math.PI / 2, 'z'], [x1, (z0 + z1) / 2, Math.PI / 2, 'z'], [0, z0, Math.PI, 'x']]) {
        s.grp();
        const a = 1.3, b = 1.0, hx = along === 'z' ? b : a, hz = along === 'z' ? a : b;
        s.box(cx - hx, 0, cz - hz, cx + hx, wh + 1.6, cz + hz, s.mat('wall'));
        s.cren(cx - hx, cz - hz, cx + hx, cz + hz, wh + 1.6);
        s.col(cx - hx, cz - hz, cx + hx, cz + hz);
        s.slot(s.light, cx, wh + 1.6, cz, yaw);
      }
      // courtyard well
      s.grp(); s.cyl(-k - 2.2, hd * 0.25, 0.7, 0, 0.8, MATS.stone(STONE)).box(-k - 2.9, 0.8, hd * 0.25 - 0.05, -k - 1.5, 2.0, hd * 0.25 + 0.05, WOOD_D, true);
    }
    s.spawn = [0, r3(hd + 1.5)];
  },

  tower(s) {
    const { hw, hd, L, fam } = s, r0 = Math.min(hw, hd);
    if (fam === 'future') return pylon(s, r0);
    const r = r0 * [0.55, 0.66, 0.78][L - 1], h = [6, 8.5, 11][L - 1];
    const top = s.towerAt(0, 0, r, h, { overhang: L >= 2 && fam === 'classic', door: true, roof: fam === 'primitive' && L >= 2 });
    s.turretFrom = [0, top + 1.2, 0];
    if (L === 1) { s.slot(s.light, 0, top, 0, 0); return; }
    if (L === 2) { s.slot(s.heavy, -r * 0.4, top, 0, 0).slot(s.light, r * 0.4, top, 0, 0); if (fam !== 'primitive') s.flag(r * 0.6, top, -r * 0.6, 2.2); return; }
    const d = r * 0.62;
    if (fam === 'classic') {
      s.grp(); const tr = r * 0.34, wallM = s.mat('wall');
      if (s.era === 'antike') { s.box(-tr, top, -tr, tr, top + 2.6, tr, wallM); s.shape('pyramid', 0, top + 3.4, 0, tr + 0.3, 0.8, tr + 0.3, s.mat('roof')); }
      else { s.cyl(0, 0, tr, top, top + 2.6, wallM); s.cone(0, 0, tr + 0.35, top + 2.6, top + 2.6 + tr * 2.4, s.mat('roof')); }
      s.flag(0, top + 2.6 + (s.era === 'antike' ? 1.5 : tr * 2.4) - 0.2, 0, 1.6);
      s.banner(0, r, 0, 0, h - 1.4, 1.0, 2.4);
    } else if (fam === 'industrial') {
      for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        s.grp(); const br = r * 0.36;
        s.cyl(sx * r, sz * r, br, top - 2.6, top, MATS.concrete(shade(s.pal.wall, 0.96)));
        s.shape('cone', sx * r, top - 3.2, sz * r, br, 0.6, br, MATS.concrete(shade(s.pal.wall, 0.9)), eulerInv([180, 0, 0], false));
        s.ring(sx * r - br, sz * r - br, sx * r + br, sz * r + br, top, top + 0.5, 0, 0.25, MATS.concrete(shade(s.pal.wall, 0.92)));
        s.slot(s.heavy, sx * (r - br * 0.2), top, sz * (r - br * 0.2), Math.atan2(sx, sz));
      }
      s.grp(); s.box(-0.9, top, -0.9, 0.9, top + 1.8, 0.9, MATS.concrete(s.pal.wall));
      s.face(0, 0.9, 0, -0.7, 0.7, top + 1.1, top + 1.4, DARK); s.box(0.5, top + 1.8, 0.5, 0.6, top + 4.2, 0.6, DARK, true);
      s.glow(1).box(0.45, top + 4.2, 0.45, 0.65, top + 4.4, 0.65, RED, true).glow(0);
    }
    if (fam !== 'industrial') for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) s.slot(sx * sz > 0 ? s.heavy : s.light, sx * d, top, sz * d, Math.atan2(sx, sz));
  },

  wall(s) {
    const { hw, hd, L, fam } = s;
    const t = Math.min(s.D * [0.5, 0.6, 0.6][L - 1], 2.4), h = [3.5, 5, 6][L - 1];
    let xa = -hw, xb = hw;
    if (L === 3) {
      const tr = Math.min(hd, 1.5);
      for (const sx of [-1, 1]) { const top = s.towerAt(sx * (hw - tr), 0, tr, h + 2, { overhang: fam === 'classic' }); s.slot(s.heavy, sx * (hw - tr), top, 0, 0); }
      xa = -hw + tr * 2 - 0.2; xb = hw - tr * 2 + 0.2;
    }
    s.wallSeg(xa, -t / 2, xb, t / 2, h);
    if (L >= 2 && fam === 'classic') {
      for (let x = xa + 1.5; x < xb - 1; x += 3) s.box(x - 0.35, 0, -t / 2 - 0.5, x + 0.35, h * 0.65, -t / 2, s.mat('wall'));
      for (const x of L === 3 ? [-hw * 0.3, hw * 0.3] : [0]) s.banner(x, t / 2, 0, 0, h - 0.6, 1.0, 2.4);
    }
    const n = L === 1 ? 1 : 2;
    for (let p = 0; p < n; p++) s.slot(s.light, n === 1 ? 0 : (p ? 1 : -1) * (xb - xa) * 0.22, h, 0, 0);
  },

  gate(s) {
    const { hw, hd, L, fam } = s;
    const gw = Math.min(4, s.W * 0.34), wt = Math.min(s.D * 0.45, 2.2), wh = [4, 4.5, 5.5][L - 1];
    if (L === 1) {
      s.wallSeg(-hw, -wt / 2, -gw / 2 - 0.7, wt / 2, wh);
      s.wallSeg(gw / 2 + 0.7, -wt / 2, hw, wt / 2, wh);
      const top = s.gateway(0, 0, gw, wh - 0.6, wt + 0.4, 0.7);
      s.slot(s.light, 0, top, 0, 0);
    } else {
      const tq = Math.min(hd * 0.9, 1.8), tx = gw / 2 + tq, th = wh + (L === 2 ? 2.2 : 3.4);
      s.wallSeg(-hw, -wt / 2, -tx - tq + 0.1, wt / 2, wh);
      s.wallSeg(tx + tq - 0.1, -wt / 2, hw, wt / 2, wh);
      for (const sx of [-1, 1]) {
        const top = s.towerAt(sx * tx, 0, tq, th, { square: fam !== 'classic' || s.era === 'antike', overhang: L === 3 && fam === 'classic' });
        s.slot(L === 2 ? s.light : s.heavy, sx * tx, top, 0, 0);
        if (L === 3 && fam === 'classic') s.banner(sx * tx, tq, 0, 0, th - 1.0, 1.1, 3.0);
      }
      const top = s.gateway(0, 0, gw, wh - 0.4, wt + 0.6, 0);
      if (L === 3 && fam === 'classic') {
        s.grp(); s.box(-gw / 2, top, -wt / 2, gw / 2, top + 1.6, wt / 2, s.mat('wall'));
        s.roofGable(-gw / 2 - 0.1, gw / 2 + 0.1, -wt / 2, wt / 2, top + 1.6, 1.5, 'x', s.mat('roof'), s.mat('wall'), 0.25);
        s.window(0, wt / 2, 0, 0, top + 0.5, 0.6, 0.8);
      } else s.slot(s.light, 0, top, 0, 0);
    }
    s.spawn = [0, r3(hd + 1.5)];
  },

  palisade(s) {
    const { hw, hd, L, fam } = s;
    if (fam === 'industrial') {
      const h = [0.85, 1.25, 1.25][L - 1], t = Math.min(s.D * 0.5, 1.0);
      s.grp(); s.sandbags(-hw, -t / 2, hw, t / 2, h); s.col(-hw, -t / 2, hw, t / 2);
      if (L >= 2) { s.grp(); s.box(-0.9, h, -t / 2, 0.9, h + 0.15, t / 2, MATS.corrugated(0x7a7f74)); s.box(-0.9, 0, -t / 2 - 0.4, -0.75, h + 0.15, -t / 2 - 0.25, DARK, true).box(0.75, 0, -t / 2 - 0.4, 0.9, h + 0.15, -t / 2 - 0.25, DARK, true); }
      if (L === 3) {
        for (const sx of [-1, 1]) { s.grp(); s.sandbags(sx * hw - (sx > 0 ? t : 0), -hd, sx * hw + (sx < 0 ? t : 0), -t / 2, h); s.col(sx * hw - (sx > 0 ? t : 0), -hd, sx * hw + (sx < 0 ? t : 0), -t / 2); }
        s.grp(); const zf = Math.min(hd - 0.15, t / 2 + 0.45);
        for (let x = -hw + 0.3; x < hw - 0.3; x += 0.25) s.put(Math.floor(x / s.vs), Math.floor((0.25 + Math.abs(Math.sin(x * 4)) * 0.35) / s.vs), Math.floor(zf / s.vs), IRON);
        for (let x = -hw + 0.5; x < hw; x += 2.5) s.box(x - 0.05, 0, zf - 0.05, x + 0.05, 0.8, zf + 0.05, WOOD_D, true);
        s.grp(); s.box(hw * 0.55 - 0.5, h, -t / 2 - 0.1, hw * 0.55 + 0.3, h + 0.4, -t / 2 + 0.3, OLIVE_D);
      }
      const n = L;
      for (let p = 0; p < n; p++) s.slot('mg', n === 1 ? 0 : -hw * 0.5 + (hw * p) / (n - 1), h, 0, 0);
      return;
    }
    if (fam === 'future') {
      const h = [2, 2.6, 3.2][L - 1], step = [4, 3, 2.4][L - 1];
      s.col(-hw, -0.4, hw, 0.4);
      for (let x = -hw + 0.4; x <= hw - 0.39; x += step) { s.grp(); s.cyl(x, 0, 0.35, 0, h, s.mat('wall')); s.glow(1).shape('ellipsoid', x, h + 0.25, 0, 0.3, 0.3, 0.3, s.pal.accent).glow(0); }
      s.glow(1).box(-hw + 0.4, 0.3, -0.05, hw - 0.4, h - 0.3, 0.05, (i, j) => ((i + j) % 4 === 0 ? s.pal.accent : shade(s.pal.accent, 0.62)), true).glow(0);
      if (L >= 2) s.slot('laser', 0, h, 0, 0);
      return;
    }
    const h = [2.5, 3, 3.6][L - 1], t = 0.5, zl = Math.min(hd - t / 2, 0.35);
    s.grp(); s.logs(-hw, zl - t / 2, hw, zl + t / 2, h); s.col(-hw, zl - t / 2, hw, zl + t / 2);
    if (L >= 2) {
      s.grp(); const zb = Math.max(-hd, zl - t / 2 - 0.9);
      s.box(-hw + 0.3, 1.4, zb, hw - 0.3, 1.6, zl - t / 2, MATS.plank(s.pal.trim));
      for (let x = -hw + 0.6; x < hw - 0.3; x += 2) s.box(x - 0.12, 0, zb, x + 0.12, 1.4, zb + 0.24, MATS.log(s.pal.trim), true);
      s.slot('archer', 0, 1.6, (zb + zl - t / 2) / 2, 0);
    }
    if (L === 3) for (const sx of [-1, 1]) {
      const r = Math.min(hd, 1.0), top = s.towerAt(sx * (hw - r), 0, r, 3.2, { roof: true });
      s.slot('archer', sx * (hw - r), top, 0, 0);
    }
    if (L >= 2) for (const sx of [-1, 1]) { s.grp(); s.box(sx * hw * 0.45 - 0.05, h - 1.2, zl + t / 2, sx * hw * 0.45 + 0.05, h + 0.6, zl + t / 2 + 0.1, WOOD_D, true); s.shape('ellipsoid', sx * hw * 0.45, h + 0.6, zl + t / 2 + 0.05, 0.25, 0.22, 0.22, 0xeadfc4); }
  },

  barracks(s) {
    const { W, D, hw, hd, L, fam } = s;
    if (fam === 'industrial' && s.era === 'weltkrieg') {
      const r = Math.min(2.2, hd * 0.44), hutLen = W * (L === 3 ? 0.6 : 0.62), hx = L === 3 ? -hw + hutLen / 2 + 0.3 : 0;
      const zs = L === 1 ? [0] : [-hd + r + 0.2, hd - r - 0.2];
      for (const z of zs) {
        s.grp(); s.shape('cylx', hx, 0, z, hutLen / 2, r, r, MATS.corrugated(s.pal.trim));
        s.door(hx + hutLen / 2, z, 1, 1.2, 2); s.window(hx + hutLen / 2, z, 1, -r * 0.6, 1, 0.5, 0.6).window(hx + hutLen / 2, z, 1, r * 0.6, 1, 0.5, 0.6);
        s.box(hx + hutLen / 2 - 0.4, 0, z - 0.6, hx + hutLen / 2 + 0.2, 0.1, z + 0.6, MATS.plank(WOOD), true);
        s.col(hx - hutLen / 2, z - r, hx + hutLen / 2, z + r);
      }
      if (L >= 2) { s.grp(); s.flag(hx + hutLen / 2 + 1.5, 0, 0, 5.5); s.sandbags(hx + hutLen / 2 + 0.6, -1.4, hx + hutLen / 2 + 1.2, -0.6, 0.8); }
      if (L === 3) {
        s.grp(); const bx0 = hx + hutLen / 2 + 2.6, bx1 = hw - 0.3, bz = hd * 0.75;
        s.box(bx0, 0, -bz, bx1, 3.5, bz, MATS.brick(0x9a5a44)); s.cren(bx0, -bz, bx1, bz, 3.5);
        s.door((bx0 + bx1) / 2, bz, 0, 1.2, 2.1); s.window(bx0, 0, 3, -bz * 0.5, 1.5).window(bx0, 0, 3, bz * 0.5, 1.5);
        s.box(bx1 - 0.8, 3.5, -bz + 0.5, bx1 - 0.7, 8, -bz + 0.6, DARK, true); s.line(bx1 - 0.75, 8, -bz + 0.55, bx0 + 0.3, 3.6, -bz + 0.3, IRON);
        s.col(bx0, -bz, bx1, bz);
      }
      s.spawn = [r3(L === 3 ? hx + hutLen / 2 + 1.2 : hutLen / 2 + 1.2), r3(hd + 1)];
      return;
    }
    if (fam === 'industrial') {
      const w = W * [0.6, 0.8, 0.88][L - 1], d = D * [0.5, 0.55, 0.42][L - 1], h = [3.5, 6.5, 9.5][L - 1], cz = L === 3 ? -hd + d / 2 + 0.3 : -D * 0.1;
      s.grp(); s.box(-w / 2, 0, cz - d / 2, w / 2, h, cz + d / 2, MATS.concrete(s.pal.wall));
      s.cren(-w / 2, cz - d / 2, w / 2, cz + d / 2, h);
      for (let y = 1.2; y < h - 0.8; y += 3) for (const [dir, zf] of [[0, cz + d / 2], [2, cz - d / 2]]) s.face(0, zf, dir, -w / 2 + 0.6, w / 2 - 0.6, y, y + 1.2, (i) => (i % 6 === 0 ? s.pal.trim : GLASS));
      s.ring(-w / 2, cz - d / 2, w / 2, cz + d / 2, h - 0.6, h - 0.3, s.vs, s.vs * 0.5, T);
      s.door(0, cz + d / 2, 0, 1.8, 2.4); s.box(-1.4, 2.5, cz + d / 2, 1.4, 2.7, cz + d / 2 + 1.2, s.pal.trim, true);
      s.box(w * 0.2, h, cz - 0.6, w * 0.2 + 1.2, h + 0.9, cz + 0.4, STEEL_D);
      s.col(-w / 2, cz - d / 2, w / 2, cz + d / 2);
      if (L >= 2) s.flag(w / 2 - 0.4, 0, cz + d / 2 + 0.8, Math.min(h + 2, 8));
      if (L === 3) {
        s.grp(); const wx0 = -w / 2, wx1 = -w / 2 + W * 0.3, wz1 = hd - 0.3;
        s.box(wx0, 0, cz + d / 2, wx1, 4, wz1, MATS.concrete(s.pal.wall)); s.cren(wx0, cz + d / 2, wx1, wz1, 4);
        s.face((wx0 + wx1) / 2, wz1, 0, -(wx1 - wx0) / 2 + 0.5, (wx1 - wx0) / 2 - 0.5, 1.4, 2.6, GLASS);
        s.col(wx0, cz + d / 2, wx1, wz1);
        s.grp(); s.box(-w * 0.3, h, cz - d * 0.35, w * 0.1, h + 0.12, cz + d * 0.35, 0x4a4f56);
        const hx0 = -w * 0.1, hz0 = cz; s.box(hx0 - 1.3, h + 0.12, hz0 - 0.15, hx0 - 0.95, h + 0.2, hz0 + 1.1, WHITE, true).box(hx0 + 0.95, h + 0.12, hz0 - 0.15, hx0 + 1.3, h + 0.2, hz0 + 1.1, WHITE, true).box(hx0 - 1.0, h + 0.12, hz0 + 0.35, hx0 + 1.0, h + 0.2, hz0 + 0.6, WHITE, true);
        s.shape('dome', w * 0.35, h, cz, 1.0, 1.0, 1.0, WHITE);
      }
      s.spawn = [0, r3(hd + 1)];
      return;
    }
    // hall: classic / primitive / future
    const w = W * [0.6, 0.75, 0.85][L - 1], d = D * [0.5, 0.55, 0.6][L - 1], h = [3, 3.5, 5.5][L - 1], cz = -D * 0.12;
    const x0 = -w / 2, x1 = w / 2, z0 = cz - d / 2, z1 = cz + d / 2;
    s.grp(); const wallM = fam === 'future' ? s.mat('wall') : s.mat('wall');
    s.box(x0, 0, z0, x1, h, z1, wallM); s.col(x0, z0, x1, z1);
    if (fam === 'classic' && s.era === 'mittelalter' && L === 3) s.timber(x0, z0, x1, z1, h, [h * 0.5]);
    if (fam === 'future') { s.shape('cylx', 0, h, cz, w / 2, d * 0.45, d / 2, s.mat('roof')); s.glow(1).ring(x0, z0, x1, z1, h - 0.3, h - 0.1, s.vs, 0, s.pal.accent).glow(0); }
    else s.roofGable(x0, x1, z0, z1, h, d * 0.55, 'x', s.mat('roof'), wallM);
    s.door(0, z1, 0, 1.6, 2.2);
    for (let a = -w / 2 + 1.4; a < w / 2 - 1; a += 2.2) if (Math.abs(a) > 1.4) { s.window(0, z1, 0, a, 1.2, 0.6, 0.9); if (L === 3) s.window(0, z1, 0, a, h * 0.5 + 1, 0.6, 0.9); }
    if (L >= 2) {
      if (fam !== 'future') { s.grp(); s.box(x1 - 1.4, h + d * 0.3, cz - 0.9, x1 - 0.8, h + d * 0.55 + 0.9, cz - 0.3, MATS.brick(0x8a6a5a)); }
      s.grp(); const yz = Math.min(hd - 0.6, z1 + 1.6);
      for (const x of [-w * 0.3, -w * 0.12]) { s.box(x - 0.08, 0, yz - 0.08, x + 0.08, 1.9, yz + 0.08, WOOD_D, true); s.box(x - 0.6, 1.3, yz - 0.06, x + 0.6, 1.45, yz + 0.06, WOOD_D, true); s.shape('ellipsoid', x, 1.6, yz, 0.25, 0.45, 0.22, fam === 'future' ? STEEL : 0xd6b25a); s.box(x - 0.2, 1.5, yz + 0.15, x + 0.2, 1.7, yz + 0.25, T, true); }
      s.box(w * 0.2, 0, yz - 0.1, w * 0.2 + 1.6, 1.4, yz + 0.1, s.pal.trim, true);
      s.flag(x0 + 0.5, 0, Math.min(hd - 0.5, z1 + 0.8), h + 2.5);
    }
    if (L === 3) {
      s.grp(); const tx = x0 + 1.4, tz = z0 + 1.4, tq = 1.5;
      s.box(tx - tq, 0, tz - tq, tx + tq, h + 3, tz + tq, wallM);
      if (fam === 'future') s.shape('dome', tx, h + 3, tz, tq, tq, tq, s.mat('roof'));
      else s.shape('pyramid', tx, h + 3 + tq, tz, tq + 0.3, tq, tq + 0.3, s.mat('roof'));
      s.col(tx - tq, tz - tq, tx + tq, tz + tq);
      s.slot(s.light, x1 - 1, h + d * 0.55, cz, 0);
    }
    s.spawn = [0, r3(hd + 1)];
  },

  house(s) {
    const { W, D, hw, hd, L, fam } = s;
    if (fam === 'primitive') return TPL.hut(s);
    const w = W * [0.58, 0.68, 0.74][L - 1], d = D * [0.48, 0.56, 0.6][L - 1], h = [2.75, 3.25, 5.25][L - 1];
    const cx = L === 3 ? -W * 0.07 : 0, cz = -D * 0.08, x0 = cx - w / 2, x1 = cx + w / 2, z0 = cz - d / 2, z1 = cz + d / 2;
    const plaster = s.era === 'mittelalter' || s.era === 'antike';
    const wallM = plaster ? MATS.plaster(s.pal.wall) : fam === 'industrial' ? MATS.brick(s.pal.wall) : s.mat('wall');
    s.grp(); s.box(x0, 0, z0, x1, h, z1, wallM); s.col(x0, z0, x1, z1);
    if (s.era === 'mittelalter') s.timber(x0, z0, x1, z1, h, L === 3 ? [h * 0.5] : []);
    const rh = d * 0.62;
    if (fam === 'future') { s.shape('dome', cx, h, cz, w / 2, d * 0.5, d / 2, s.mat('roof')); s.glow(1).ring(x0, z0, x1, z1, h - 0.25, h - 0.1, s.vs, 0, s.pal.accent).glow(0); }
    else if (s.era === 'moderne') { s.box(x0, h, z0, x1, h + 0.3, z1, s.pal.roof); s.cren(x0, z0, x1, z1, h + 0.3); }
    else s.roofGable(x0, x1, z0, z1, h, rh, 'x', s.mat('roof', s.era === 'antike' || s.era === 'mittelalter' ? 'tile' : undefined), wallM);
    s.door(cx + (L === 1 ? 0 : -w * 0.22), z1, 0, 1.0, 2.0);
    const wins = L === 1 ? [w * 0.25] : [w * 0.12, w * 0.32];
    for (const a of wins) {
      s.window(cx, z1, 0, a, 1.0, 0.6, 0.8);
      if (L >= 2) s.face(cx, z1, 0, a - 0.45, a + 0.45, 0.6, 0.8, s.pal.accent, -0.3 * s.vs, s.vs * 1.8);
    }
    s.window(x1, cz, 1, 0, 1.0, 0.6, 0.8).window(x0, cz, 3, 0, 1.0, 0.6, 0.8).window(cx, z0, 2, 0, 1.0, 0.6, 0.8);
    if (L === 3) for (const a of [-w * 0.25, 0, w * 0.25]) s.window(cx, z1, 0, a, h * 0.5 + 0.7, 0.6, 0.8);
    if (L >= 2 && fam !== 'future') { s.grp(); s.box(x1 - 1.2, h + 0.5, cz - 0.6, x1 - 0.6, h + rh + 0.7, cz - 0.05, MATS.brick(0x8a5a4a)); s.box(x1 - 1.3, h + rh + 0.7, cz - 0.7, x1 - 0.5, h + rh + 0.9, cz + 0.05, DARK, true); }
    if (L === 3) {
      const sw = Math.min(1.6, hw - x1 - 0.15);
      if (sw > 0.6) {
        s.grp(); s.box(x1, 0, z0 + 0.3, x1 + sw, 2.2, z0 + 0.3 + d * 0.7, MATS.plank(WOOD));
        s.shape('gablez', x1 + sw / 2, 2.2 + 0.5, z0 + 0.3 + d * 0.35, sw / 2 + 0.2, 0.5, d * 0.35 + 0.2, s.mat('roof'));
        s.col(x1, z0 + 0.3, x1 + sw, z0 + 0.3 + d * 0.7);
      }
      s.grp(); const zf = hd - 0.25;
      for (let x = -hw + 0.3; x <= hw - 0.3; x += 0.75) if (Math.abs(x - (cx - w * 0.22)) > 0.7) s.box(x - 0.08, 0, zf - 0.08, x + 0.08, 0.8, zf + 0.08, WHITE, true);
      s.box(-hw + 0.3, 0.45, zf - 0.04, cx - w * 0.22 - 0.7, 0.6, zf + 0.04, WHITE, true).box(cx - w * 0.22 + 0.7, 0.45, zf - 0.04, hw - 0.3, 0.6, zf + 0.04, WHITE, true);
      s.shape('ellipsoid', hw - 1.0, 0.5, z1 + 0.8, 0.55, 0.5, 0.55, LEAF);
    }
    s.spawn = [r3(cx - w * 0.22), r3(hd + 1)];
  },

  hut(s) {
    const { hw, hd, L } = s, R0 = Math.min(hw, hd);
    const huts = [[-0.15, -0.15, 0.5]];
    if (L >= 2) huts.push([-0.56, 0.6, 0.31]);
    if (L === 3) huts.push([0.62, 0.36, 0.33]);
    for (const [nx, nz, nr] of huts) {
      s.grp(); const cx = nx * hw, cz = nz * hd, r = nr * R0, wh = r * 0.8;
      s.cyl(cx, cz, r, 0, wh, MATS.log(s.pal.wall));
      s.cone(cx, cz, r + 0.45, wh - 0.2, wh + r * 1.5, s.mat('roof', s.fam === 'primitive' ? 'thatch' : undefined));
      s.box(cx - 0.1, wh + r * 1.5 - 0.3, cz - 0.1, cx + 0.1, wh + r * 1.5 + 0.4, cz + 0.1, WOOD_D, true);
      s.door(cx, cz + r, 0, Math.min(1.0, r * 0.6), Math.min(1.6, wh));
      if (L >= 2) s.face(cx, cz + r, 0, -0.5, 0.5, wh * 0.85, wh + 0.1, T, -0.2 * s.vs, 1.6 * s.vs);
      s.col(cx - r * 0.85, cz - r * 0.85, cx + r * 0.85, cz + r * 0.85);
    }
    if (L >= 2) {
      s.grp(); const fx = 0.08 * hw, fz = 0.68 * hd, fr = 0.15 * R0;
      for (let a = 0; a < 8; a++) { const t = (a / 8) * Math.PI * 2; s.box(fx + Math.cos(t) * fr - 0.12, 0, fz + Math.sin(t) * fr - 0.12, fx + Math.cos(t) * fr + 0.12, 0.25, fz + Math.sin(t) * fr + 0.12, STONE_D, true); }
      s.glow(1).shape('cone', fx, 0.45, fz, fr * 0.55, 0.45, fr * 0.55, 0xff9a30).box(fx - 0.1, 0.1, fz - 0.1, fx + 0.1, 1.0, fz + 0.1, 0xffd23c, true).glow(0);
    }
    if (L === 3) {
      s.grp(); const tx = 0.62 * hw, tz = -0.68 * hd;
      for (let y = 0, n = 0; y < 3.6; y += 0.9, n++) s.box(tx - 0.3, y, tz - 0.3, tx + 0.3, y + 0.85, tz + 0.3, n & 1 ? T : MATS.log(WOOD_L));
      s.glow(1).box(tx - 0.2, 2.9, tz + 0.3, tx - 0.05, 3.05, tz + 0.4, 0xffd23c, true).box(tx + 0.05, 2.9, tz + 0.3, tx + 0.2, 3.05, tz + 0.4, 0xffd23c, true).glow(0);
      s.box(tx - 0.9, 3.0, tz - 0.08, tx + 0.9, 3.3, tz + 0.08, WOOD_L, true);
      s.col(tx - 0.35, tz - 0.35, tx + 0.35, tz + 0.35);
      s.grp(); const rx = -0.62 * hw, rz = -0.7 * hd;
      s.box(rx - 0.9, 0, rz - 0.06, rx - 0.78, 1.6, rz + 0.06, WOOD_D, true).box(rx + 0.78, 0, rz - 0.06, rx + 0.9, 1.6, rz + 0.06, WOOD_D, true).box(rx - 0.9, 1.5, rz - 0.06, rx + 0.9, 1.6, rz + 0.06, WOOD_D, true);
      s.box(rx - 0.6, 0.6, rz - 0.04, rx + 0.6, 1.5, rz + 0.04, 0xb8865a, true);
    }
    s.spawn = [r3(-0.15 * hw), r3(hd + 1)];
  },

  farm(s) {
    const { W, D, hw, hd, L, fam } = s;
    const field = (x0, z0, x1, z1, crop) => {
      s.grp(); s.box(x0, 0, z0, x1, 0.12, z1, (i) => (i & 1 ? SOIL : shade(SOIL, 0.85)), true);
      const [i0, i1] = s.rng(x0 + 0.25, x1 - 0.25), [k0, k1] = s.rng(z0 + 0.25, z1 - 0.25), j0 = s.rng(0.12, 0.12)[1] + 1;
      for (let i = i0; i <= i1; i++) if ((i - i0) % 2 === 0) for (let k = k0; k <= k1; k++) {
        const hh = crop === 2 ? ((k & 1) ? 0 : 1) : 1 + (hash3(i, 1, k) > 0.6 ? 1 : 0);
        for (let j = j0; j <= j0 + hh; j++) s.put(i, j, k, crop === 0 ? (j === j0 + hh ? WHEAT : 0xa8963a) : crop === 1 ? (j === j0 + hh ? 0x7ab84a : LEAF) : (j === j0 + hh && hh ? 0xf28a2a : LEAF));
      }
      if (fam !== 'future') {
        for (let x = x0; x <= x1 + 1e-6; x += (x1 - x0) / Math.max(1, Math.round((x1 - x0) / 1.5))) for (const z of [z0, z1]) s.box(x - 0.06, 0, z - 0.06, x + 0.06, 0.7, z + 0.06, WOOD, true);
        for (let z = z0; z <= z1 + 1e-6; z += (z1 - z0) / Math.max(1, Math.round((z1 - z0) / 1.5))) for (const x of [x0, x1]) s.box(x - 0.06, 0, z - 0.06, x + 0.06, 0.7, z + 0.06, WOOD, true);
        for (const z of [z0, z1]) s.box(x0, 0.45, z - 0.04, x1, 0.55, z + 0.04, WOOD_L, true);
        for (const x of [x0, x1]) s.box(x - 0.04, 0.45, z0, x + 0.04, 0.55, z1, WOOD_L, true);
      } else s.glow(1).ring(x0, z0, x1, z1, 0, 0.15, 0.1, 0, s.pal.accent).glow(0);
    };
    field(hw * 0.08, hd * 0.12, hw - 0.4, hd - 0.4, 0);
    if (L >= 2) field(-hw + 0.4, hd * 0.12, -hw * 0.08, hd - 0.4, 1);
    if (L === 3) field(hw * 0.08, -hd + 0.4, hw * 0.52, -hd * 0.12, 2);
    // shed (L1) or barn (L2+), back-left
    s.grp();
    const bw = L === 1 ? 3 : W * 0.38, bd = L === 1 ? 2.6 : D * 0.36, bh = L === 1 ? 2 : 3.2;
    const bx0 = -hw + 0.6, bz0 = -hd + 0.6, bx1 = bx0 + bw, bz1 = bz0 + bd, wallM = fam === 'industrial' ? MATS.corrugated(s.pal.wall) : MATS.plank(s.pal.wall);
    s.box(bx0, 0, bz0, bx1, bh, bz1, wallM); s.col(bx0, bz0, bx1, bz1);
    if (fam === 'future') s.shape('dome', (bx0 + bx1) / 2, bh, (bz0 + bz1) / 2, bw / 2, bd * 0.4, bd / 2, s.mat('roof'));
    else s.roofGable(bx0, bx1, bz0, bz1, bh, bd * 0.55, 'x', s.mat('roof', fam === 'classic' ? 'tile' : undefined), wallM);
    if (L >= 2) {
      const dc = (bx0 + bx1) / 2;
      s.face(dc, bz1, 0, -1.1, 1.1, 0, 2.4, WHITE); s.face(dc, bz1, 0, -0.95, 0.95, 0, 2.25, (i, j) => (j % 3 === 0 ? WHITE : shade(s.pal.wall, 0.75)));
      s.line(dc - 0.95, 0.1, bz1 + s.vs * 0.5, dc + 0.95, 2.2, bz1 + s.vs * 0.5, WHITE).line(dc + 0.95, 0.1, bz1 + s.vs * 0.5, dc - 0.95, 2.2, bz1 + s.vs * 0.5, WHITE);
      s.face(dc, bz1, 0, -0.4, 0.4, bh + 0.3, bh + 0.9, WOOD_D);
    } else s.door((bx0 + bx1) / 2, bz1, 0, 1, 1.7);
    if (L === 3) {
      const mx = hw * 0.78, mz = -hd * 0.55, mr = Math.min(1.4, hw * 0.18);
      s.grp();
      if (fam === 'classic' || fam === 'primitive') {
        s.cyl(mx, mz, mr, 0, 6, MATS.plaster(0xefe6d2)); s.cone(mx, mz, mr + 0.3, 6, 8, s.mat('roof', 'tile'));
        s.door(mx, mz + mr, 0, 0.8, 1.6);
        const hub = [mx, 6.2, mz + mr + 0.4];
        s.box(hub[0] - 0.15, hub[1] - 0.15, hub[2] - 0.3, hub[0] + 0.15, hub[1] + 0.15, hub[2] + 0.1, WOOD_D, true);
        for (const [dx, dy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
          const ex = hub[0] + dx * 2.6 * 0.7071, ey = hub[1] + dy * 2.6 * 0.7071;
          s.line(hub[0], hub[1], hub[2], ex, Math.max(0.4, ey), hub[2], WOOD_D);
          s.line(hub[0] + dx * 0.5, hub[1] + dy * 0.9, hub[2], ex + dx * 0.35, Math.max(0.5, ey + dy * 0.05 - dx * dy * 0.4), hub[2], WHITE);
        }
      } else if (fam === 'industrial') {
        s.cyl(mx, mz, mr, 0, 7, MATS.corrugated(0xb8bcc0)); s.shape('dome', mx, 7, mz, mr, mr * 0.6, mr, STEEL);
      } else { s.shape('dome', mx, 0, mz, mr * 1.4, mr * 1.4, mr * 1.4, shade(s.pal.accent, 0.55)); s.glow(1).ring(mx - mr, mz - mr, mx + mr, mz + mr, 0, 0.2, 0.3, 0, s.pal.accent).glow(0); }
      s.col(mx - mr, mz - mr, mx + mr, mz + mr);
      s.grp(); for (const [hx, hz] of [[-hw * 0.1, -hd * 0.45], [hw * 0.15, -hd * 0.62]]) s.shape('ellipsoid', hx, 0.35, hz, 0.7, 0.75, 0.7, WHEAT);
    }
    s.spawn = [0, r3(hd + 1)];
  },

  workshop(s) {
    const { W, D, hw, hd, L, fam } = s;
    const w = W * [0.55, 0.6, 0.68][L - 1], d = D * [0.5, 0.55, 0.58][L - 1], h = [3, 3.4, 4.6][L - 1];
    const cx = -W * 0.08, cz = -D * 0.1, x0 = cx - w / 2, x1 = cx + w / 2, z0 = cz - d / 2, z1 = cz + d / 2;
    const wallM = fam === 'classic' ? MATS.plank(s.era === 'antike' ? s.pal.wall : WOOD) : s.mat('wall');
    s.grp(); s.box(x0, 0, z0, x1, h, z1, wallM); s.col(x0, z0, x1, z1);
    if (fam === 'classic') s.ring(x0, z0, x1, z1, 0, 0.9, 0.2, 0, MATS.stone(STONE));
    if (fam === 'future') s.shape('cylx', cx, h, cz, w / 2, d * 0.4, d / 2, s.mat('roof'));
    else if (fam === 'industrial') { s.box(x0, h, z0, x1, h + 0.3, z1, s.pal.roof); for (let x = x0 + 0.8; x < x1 - 0.5; x += 2.4) s.shape('gablex', x + 0.6, h + 0.9, cz, 0.6, 0.6, d / 2, s.mat('roof')); }
    else s.roofGable(x0, x1, z0, z1, h, d * 0.5, 'x', s.mat('roof'), wallM);
    s.face(cx, z1, 0, -1.4, 1.4, 0, 2.6, s.pal.trim);
    s.face(cx, z1, 0, -1.2, 1.2, 0, 2.4, (i, j) => (j % 4 === 1 ? IRON : (i & 1 ? WOOD_D : 0x4a3220)));
    s.window(cx, z1, 0, -w * 0.33, 1.2, 0.6, 0.8);
    s.grp(); const chx = x0 + 0.9, chz = z0 + 0.8, chTop = h + d * 0.5 + 1.2;
    if (fam === 'industrial') s.cyl(chx, chz, 0.5, 0, h + 5, MATS.brick(0x9a5a44));
    else s.box(chx - 0.4, 0, chz - 0.4, chx + 0.4, chTop, chz + 0.4, MATS.stone(STONE_D));
    s.glow(1).box(chx - 0.25, (fam === 'industrial' ? h + 5 : chTop) - 0.05, chz - 0.25, chx + 0.25, (fam === 'industrial' ? h + 5 : chTop) + 0.15, chz + 0.25, 0xff7a20, true).glow(0);
    s.grp(); const ax = cx + w * 0.3, az = z1 + 1.0;
    s.box(ax - 0.25, 0, az - 0.2, ax + 0.25, 0.55, az + 0.2, WOOD_D, true).box(ax - 0.45, 0.55, az - 0.18, ax + 0.45, 0.8, az + 0.18, IRON, true);
    if (L >= 2) {
      s.grp(); const lx0 = x1, lx1 = Math.min(hw - 0.3, x1 + 2.6);
      for (const z of [z0 + 0.3, z1 - 0.3]) s.box(lx1 - 0.12, 0, z - 0.12, lx1 + 0.12, 2.2, z + 0.12, WOOD_D, true);
      s.shape('box', (lx0 + lx1) / 2, 2.35, cz, (lx1 - lx0) / 2 + 0.2, 0.12, d / 2, s.mat('roof'), eulerInv([0, 0, -12], false));
      s.box(lx0 + 0.3, 0.8, cz - 1, lx0 + 1.1, 0.95, cz + 1, WOOD_L, true);
      for (let n = 0; n < 3; n++) s.cyl(lx0 + 1.4, cz + 1.4 - n * 0.7, 0.3, 0, 0.9, n === 1 ? T : MATS.plank(WOOD));
      s.col(lx0, z0, lx1, z1);
      s.grp(); for (let n = 0; n < 4; n++) s.shape('cylx', -hw + 1.6, 0.25 + (n > 2 ? 0.45 : 0), hd - 1.4 - (n % 3) * 0.5, 1.2, 0.24, 0.24, MATS.log(WOOD));
    }
    if (L === 3) {
      s.grp(); const kx = hw - 1.0, kz = -hd + 1.0, top = 7.5;
      s.box(kx - 0.2, 0, kz - 0.2, kx + 0.2, top, kz + 0.2, MATS.log(WOOD_D), true);
      s.line(kx, top - 0.2, kz, kx - 3.4, top - 1.6, kz - 0.3, WOOD_D).line(kx, top - 0.5, kz, kx - 3.4, top - 1.9, kz - 0.3, WOOD_D);
      s.line(kx - 3.3, top - 1.8, kz - 0.3, kx - 3.3, 2.0, kz - 0.3, 0xd9c8a0);
      s.box(kx - 3.8, 1.2, kz - 0.8, kx - 2.8, 2.0, kz + 0.2, MATS.plank(WOOD_L), true);
      s.flag(kx, top, kz, 1.0);
      s.col(kx - 0.3, kz - 0.3, kx + 0.3, kz + 0.3);
    }
    s.spawn = [r3(cx), r3(hd + 1)];
  },

  bunker(s) {
    const { W, D, hw, hd, L, fam } = s;
    const w = W * [0.5, 0.62, 0.66][L - 1], d = D * [0.5, 0.58, 0.6][L - 1], h = [1.8, 2.2, 2.4][L - 1];
    const wallM = fam === 'future' ? s.mat('wall') : MATS.concrete(s.pal.wall);
    s.grp(); s.box(-w / 2, 0, -d / 2, w / 2, h, d / 2, wallM);
    s.box(-w / 2 - 0.3, h, -d / 2 - 0.3, w / 2 + 0.3, h + 0.45, d / 2 + 0.3, MATS.concrete(shade(s.pal.wall, 0.92)));
    if (fam === 'future') s.glow(1);
    s.ring(-w / 2, -d / 2, w / 2, d / 2, h - 0.75, h - 0.45, s.vs * 0.5, s.vs * 0.6, fam === 'future' ? s.pal.accent : DARK);
    s.glow(0);
    s.face(0, -d / 2, 2, -0.5, 0.5, 0, 1.6, DARK);
    s.col(-w / 2 - 0.3, -d / 2 - 0.3, w / 2 + 0.3, d / 2 + 0.3);
    s.turretFrom = [0, h - 0.6, d / 2 + 0.2];
    if (L === 1) { s.slot(s.light, 0, h + 0.45, 0, 0); return; }
    s.grp(); const rw = Math.min(hw - 0.2, w / 2 + 1.0), rd = Math.min(hd - 0.2, d / 2 + 1.0);
    if (fam === 'future') s.glow(1).ring(-rw, -rd, rw, rd, 0, 0.3, 0, 0.3, s.pal.accent).glow(0);
    else {
      s.sandbags(-rw, rd - 0.6, rw, rd, 0.9); s.sandbags(-rw, -rd, -rw + 0.6, rd - 0.6, 0.9); s.sandbags(rw - 0.6, -rd, rw, rd - 0.6, 0.9);
      s.col(-rw, rd - 0.6, rw, rd); s.col(-rw, -rd, -rw + 0.6, rd - 0.6); s.col(rw - 0.6, -rd, rw, rd - 0.6);
    }
    s.slot(s.light, -w * 0.25, h + 0.45, 0, -0.5).slot(s.light, w * 0.25, h + 0.45, 0, 0.5);
    if (L === 3) {
      s.grp(); s.shape('dome', -w * 0.2, h + 0.45, -d * 0.2, 0.9, 0.8, 0.9, fam === 'future' ? s.pal.trim : STEEL_D);
      s.glow(1).box(-w * 0.2 - 0.4, h + 0.75, -d * 0.2 + 0.75, -w * 0.2 + 0.4, h + 0.9, -d * 0.2 + 0.95, fam === 'future' ? s.pal.accent : 0xffd23c, true).glow(0);
      s.box(w * 0.35, h + 0.45, -d * 0.35, w * 0.35 + 0.08, h + 3.6, -d * 0.35 + 0.08, DARK, true);
      if (fam === 'industrial') s.paint(-w / 2 - 0.4, 0, -d / 2 - 0.4, w / 2 + 0.4, h + 0.5, d / 2 + 0.4, (i, j, k) => (hash3(i >> 2, j >> 2, k >> 2) > 0.5 ? OLIVE : OLIVE_D), (i, j, k) => hash3(i >> 2, j >> 2, k >> 2) > 0.62 || hash3(i >> 2, j >> 2, k >> 2) < 0.2);
      s.slot(s.heavy, w * 0.15, h + 0.45, d * 0.25, 0);
    }
  },

  reactor(s) {
    const { hw, hd, L } = s, sx = hw / 11, sz = hd / 9, sc = Math.min(sx, sz);
    const ccx = -5.6 * sx, ccz = -1.6 * sz, cr = 3.2 * sc, ch = 5;
    s.grp(); s.cyl(ccx, ccz, cr, 0, ch, MATS.concrete(s.pal.wall)); s.shape('dome', ccx, ch, ccz, cr, cr * 0.85, cr, MATS.concrete(shade(s.pal.wall, 1.05)));
    s.cyl(ccx, ccz, cr + 0.2, 0, 0.6, shade(s.pal.wall, 0.8));
    s.door(ccx, ccz + cr, 0, 1.2, 2.2);
    s.col(ccx - cr, ccz - cr, ccx + cr, ccz + cr);
    s.grp(); const ax0 = ccx + cr - 0.4, ax1 = ccx + cr + 2.4 * sc;
    s.box(ax0, 0, ccz - 1.6 * sc, ax1, 3, ccz + 1.6 * sc, MATS.concrete(shade(s.pal.wall, 0.95))); s.cren(ax0, ccz - 1.6 * sc, ax1, ccz + 1.6 * sc, 3);
    s.col(ax0, ccz - 1.6 * sc, ax1, ccz + 1.6 * sc);
    trefoil(s, (ax0 + ax1) / 2 + 0.2, 1.6, ccz + 1.6 * sc + s.vs * 0.5, Math.min(1.1, (ax1 - ax0) * 0.35));
    s.glow(1).box(ccx - 0.4, ch + cr * 0.85 - 0.1, ccz - 0.4, ccx + 0.4, ch + cr * 0.85 + 0.25, ccz + 0.4, 0x7cff4f, true).glow(0);
    const cool = (x, z, R, H) => {
      s.grp(); const tw = 0.72, rw = R * 0.62, rt = R * 0.72;
      const rf = y => { const t = y / H; return t < tw ? rw + (R - rw) * ((tw - t) / tw) ** 2 : rw + (rt - rw) * ((t - tw) / (1 - tw)) ** 2; };
      s.tube(x, z, rf, 0, H, s.vs * 1.45, (i, j) => (j * s.vs > H - 1.2 ? shade(s.pal.wall, 0.8) : (j * s.vs > H * 0.8 && j * s.vs < H * 0.86) ? RED : MATS.concrete(0xd8d8d4)(i, j, 0)));
      for (let a = 0; a < 6; a++) { const t = (a / 6) * Math.PI * 2; s.box(x + Math.cos(t) * R * 0.95 - 0.15, 0, z + Math.sin(t) * R * 0.95 - 0.15, x + Math.cos(t) * R * 0.95 + 0.15, 1.2, z + Math.sin(t) * R * 0.95 + 0.15, shade(s.pal.wall, 0.75), true); }
      s.col(x - R * 0.9, z - R * 0.9, x + R * 0.9, z + R * 0.9);
      if (L === 3) { s.grp(); s.shape('ellipsoid', x, H + 1.3, z, rt * 1.1, 1.3, rt * 1.1, 0xf4f6f8); s.shape('ellipsoid', x + 0.8, H + 3.0, z - 0.4, rt * 0.9, 1.2, rt * 0.9, 0xe8ecf0); }
      return H;
    };
    if (L >= 2) {
      const tx = 4.2 * sx, tz = -4.2 * sz, R = 3.6 * sc;
      cool(tx, tz, R, 11 * sc + 1);
      s.grp(); s.shape('cylx', (ccx + cr + tx - R) / 2 + 0.6, 1.6, ccz - 0.6, Math.max(0.5, (tx - R - ccx - cr) / 2 + 0.6), 0.35, 0.35, STEEL);
    }
    if (L === 3) {
      const tx = 6.6 * sx, tz = 4.6 * sz, R = 3.0 * sc;
      cool(tx, tz, R, 9.5 * sc + 1);
      s.grp(); const hx0 = -10.4 * sx, hx1 = -0.6 * sx, hz0 = 3.4 * sz, hz1 = 8.4 * sz;
      s.box(hx0, 0, hz0, hx1, 4, hz1, MATS.concrete(s.pal.wall)); s.box(hx0, 4, hz0, hx1, 4.6, hz1, MATS.metal(s.pal.roof));
      for (let x = hx0 + 1; x < hx1 - 0.8; x += 1.6) s.window(x, hz1, 0, 0, 2.2, 0.7, 1.2);
      s.face((hx0 + hx1) / 2, hz1, 0, -(hx1 - hx0) / 2, (hx1 - hx0) / 2, 3.4, 3.7, T);
      s.col(hx0, hz0, hx1, hz1);
      s.grp(); s.box(hx1 - 1.2, 4.6, hz0 + 0.5, hx1 - 0.4, 9, hz0 + 1.3, MATS.brick(0xb8443a)); s.box(hx1 - 1.2, 7.2, hz0 + 0.5, hx1 - 0.4, 7.6, hz0 + 1.3, WHITE);
    }
    s.spawn = [0, r3(hd + 1.5)];
  },

  silo(s) {
    const { W, D, hw, hd, L, fam } = s;
    const missile = (x, z, r, y0, H) => {
      s.grp(); const body = fam === 'future' ? s.pal.wall : WHITE;
      s.cyl(x, z, r, y0, y0 + H * 0.78, (i, j) => (((j * s.vs - y0) / H) % 0.25 < 0.05 ? T : body));
      s.cone(x, z, r, y0 + H * 0.78, y0 + H, fam === 'future' ? s.pal.accent : RED);
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) s.box(x + dx * r - (dz ? 0.06 : 0) - (dx < 0 ? 0.7 : 0), y0, z + dz * r - (dx ? 0.06 : 0) - (dz < 0 ? 0.7 : 0), x + dx * r + (dz ? 0.06 : 0) + (dx > 0 ? 0.7 : 0), y0 + 1.6, z + dz * r + (dx ? 0.06 : 0) + (dz > 0 ? 0.7 : 0), DARK, true);
    };
    const hatch = (x, z, R, open) => {
      s.grp(); s.cyl(x, z, R + 0.6, 0, 0.5, MATS.concrete(shade(s.pal.wall, 0.9)));
      s.cyl(x, z, R, 0.3, 0.55, (i, j, k) => (((i + k) >> 1) & 1 ? s.pal.accent : DARK));
      s.cyl(x, z, R - 0.35, 0.4, 0.6, 0x15171a);
      if (open) for (const sx of [-1, 1]) s.shape('box', x + sx * (R + 0.3), 1.2, z, 0.15, 1.2, R * 0.9, MATS.metal(STEEL_D), eulerInv([0, 0, sx * 25], false));
      else s.box(x - R + 0.3, 0.55, z - R + 0.3, x + R - 0.3, 0.7, z + R - 0.3, MATS.metal(STEEL_D));
      s.col(x - R - 0.6, z - R - 0.6, x + R + 0.6, z + R + 0.6);
    };
    s.grp(); const pw = W * 0.86, pd = D * 0.86;
    s.box(-pw / 2, 0, -pd / 2, pw / 2, 0.3, pd / 2, MATS.concrete(s.pal.wall));
    const R1 = Math.min(W, D) * 0.15, m1x = -W * 0.12, m1z = -D * 0.08;
    hatch(m1x, m1z, R1, L >= 2);
    if (L >= 2) {
      missile(m1x, m1z, R1 * 0.55, 0.5, 9);
      s.grp(); const gx = m1x + R1 + 1.4;
      s.box(gx - 0.5, 0.3, m1z - 0.5, gx + 0.5, 9.5, m1z + 0.5, (i, j, k) => (((i + j + k) % 3 === 0) ? STEEL_D : STEEL));
      for (let y = 2; y < 9.5; y += 2.4) s.box(m1x + R1 * 0.55, y, m1z - 0.12, gx - 0.5, y + 0.2, m1z + 0.12, STEEL_D, true);
      s.glow(1).box(gx - 0.15, 9.5, m1z - 0.15, gx + 0.15, 9.8, m1z + 0.15, RED, true).glow(0);
      s.col(gx - 0.5, m1z - 0.5, gx + 0.5, m1z + 0.5);
    }
    s.grp(); const bx0 = W * 0.12, bx1 = hw - 0.5, bz0 = D * 0.12, bz1 = hd - 0.5, bh = 3;
    s.box(bx0, 0, bz0, bx1, bh, bz1, MATS.concrete(shade(s.pal.wall, 0.95))); s.cren(bx0, bz0, bx1, bz1, bh);
    s.door((bx0 + bx1) / 2, bz1, 0, 1, 2); s.window(bx0, (bz0 + bz1) / 2, 3, 0, 1.2, 1.2, 0.6);
    s.col(bx0, bz0, bx1, bz1);
    if (L === 3) {
      hatch(W * 0.25, -D * 0.25, R1 * 0.8, true); missile(W * 0.25, -D * 0.25, R1 * 0.45, 0.5, 7);
      s.grp(); const dx = (bx0 + bx1) / 2, dz = (bz0 + bz1) / 2;
      s.box(dx - 0.1, bh, dz - 0.1, dx + 0.1, bh + 1.2, dz + 0.1, STEEL_D, true);
      s.shape('ellipsoid', dx, bh + 1.8, dz + 0.2, 1.2, 1.2, 0.3, STEEL_L, eulerInv([-30, 0, 0], false));
      s.box(dx - 0.05, bh + 1.6, dz + 0.4, dx + 0.05, bh + 2.0, dz + 1.0, DARK, true);
    }
    s.turretFrom = [r3(m1x), 9.5, r3(m1z)];
    s.spawn = [0, r3(hd + 1.5)];
  },

  turret_base(s) {
    const { hw, hd, L, fam } = s, r = Math.min(hw, hd) * 0.7, h = [1, 1.8, 2.6][L - 1];
    s.grp();
    if (fam === 'primitive') s.logs(-r, -r, r, r, h);
    else if (fam === 'future') { s.cyl(0, 0, r, 0, h, s.mat('wall')); s.glow(1).cyl(0, 0, r + s.vs, h - 0.3, h - 0.1, s.pal.accent).glow(0); }
    else s.box(-r, 0, -r, r, h, r, s.mat('wall'));
    if (fam !== 'future') s.cren(-r, -r, r, r, h, 0.6);
    s.col(-r, -r, r, r);
    if (L >= 2 && fam !== 'future') for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) s.box(sx * r - 0.2, 0, sz * r - 0.2, sx * r + 0.2, h + 0.9, sz * r + 0.2, s.mat('wall'));
    s.slot(s.heavy, 0, h, L === 3 ? -r * 0.3 : 0, 0);
    if (L === 3) s.slot(s.light, 0, h, r * 0.45, 0);
  },

  dome(s) {
    const { hw, hd, L } = s, R0 = Math.min(hw, hd);
    const r = R0 * [0.55, 0.68, 0.8][L - 1];
    s.grp(); s.cyl(0, 0, r + 0.6, 0, 0.6, s.mat('wall'));
    s.glow(1).cyl(0, 0, r + 0.6 + s.vs, 0.35, 0.5, s.pal.accent).glow(0);
    const merid = (i, k) => Math.abs(Math.sin(4 * Math.atan2(k + 0.5, i + 0.5))) < 0.14, lat = j => j % 5 === 0;
    s.grp(); s.shape('dome', 0, 0.6, 0, r, r * 0.8, r, (i, j, k) => (lat(j) || merid(i, k) ? s.pal.trim : shade(s.pal.accent, (j & 1) ? 0.5 : 0.56)));
    s.glow(1).paint(-r, 0.6, -r, r, 0.6 + r, r, s.pal.accent, (i, j, k) => lat(j) && merid(i, k)).glow(0);
    s.door(0, r * 0.98, 0, 1.6, 2.4);
    s.col(-r * 0.85, -r * 0.85, r * 0.85, r * 0.85);
    s.grp(); s.cyl(0, 0, 0.4, 0.6 + r * 0.8 - 0.2, 0.6 + r * 0.8 + 1.4, s.pal.trim); s.glow(1).shape('ellipsoid', 0, 0.6 + r * 0.8 + 1.8, 0, 0.5, 0.5, 0.5, s.pal.accent).glow(0);
    const pyl = L === 1 ? [] : L === 2 ? [[-1, 1], [1, -1]] : [[-1, -1], [1, -1], [-1, 1], [1, 1]];
    const pd = Math.min(R0 - 0.7, r + 1.2) * 0.7071;
    for (const [sx, sz] of pyl) {
      s.grp(); const px = sx * pd, pz = sz * pd, ph = 3 + L;
      s.cyl(px, pz, 0.55, 0, 0.5, s.pal.trim); s.cyl(px, pz, 0.28, 0.5, ph, s.mat('wall'));
      s.glow(1).shape('ellipsoid', px, ph + 0.45, pz, 0.45, 0.45, 0.45, s.pal.accent).cyl(px, pz, 0.36, ph * 0.5, ph * 0.5 + 0.2, s.pal.accent).glow(0);
      s.col(px - 0.55, pz - 0.55, px + 0.55, pz + 0.55);
    }
    if (L === 3) { s.grp(); s.glow(1).tube(0, 0, () => r * 0.55, 0.6 + r * 0.8 + 0.6, 0.6 + r * 0.8 + 0.8, s.vs * 1.5, s.pal.accent).glow(0); }
    s.spawn = [0, r3(hd + 1)];
  },

  temple(s) {
    const { W, D, hd, L } = s;
    const w = W * [0.55, 0.75, 0.92][L - 1], d = D * [0.5, 0.72, 0.92][L - 1], colH = [3, 4, 5][L - 1];
    const cz = L === 1 ? D * 0.05 : 0, x0 = -w / 2, x1 = w / 2, z0 = cz - d / 2, z1 = cz + d / 2;
    const stone = MATS.stone(s.pal.wall), marble = MATS.plaster(s.pal.wall);
    s.grp(); s.col(x0, z0, x1, z1);
    s.ring(x0 + 0.01, z0 + 0.01, x1 - 0.01, z1 - 0.01, 0, 0.3, 0, 0.3, stone);
    s.ring(x0 + 0.3, z0 + 0.3, x1 - 0.3, z1 - 0.3, 0, 0.6, 0, 0.3, stone);
    s.box(x0 + 0.6, 0, z0 + 0.6, x1 - 0.6, 0.9, z1 - 0.6, stone);
    const bx0 = x0 + 0.6, bx1 = x1 - 0.6, bz0 = z0 + 0.6, bz1 = z1 - 0.6, cy = 0.9, top = cy + colH;
    const colAt = (x, z) => {
      s.box(x - 0.45, cy, z - 0.45, x + 0.45, cy + 0.25, z + 0.45, marble);
      s.cyl(x, z, 0.33, cy + 0.25, top - 0.3, (i, j, k) => ((i + k) & 1 ? shade(s.pal.wall, 0.94) : s.pal.wall));
      s.box(x - 0.5, top - 0.3, z - 0.5, x + 0.5, top, z + 0.5, s.pal.trim === T ? marble : shade(s.pal.wall, 1.02));
    };
    const nx = Math.max(2, Math.round((bx1 - bx0 - 0.9) / 2.1)), nz = Math.max(2, Math.round((bz1 - bz0 - 0.9) / 2.1));
    s.grp();
    for (let p = 0; p <= nx; p++) {
      const x = bx0 + 0.45 + ((bx1 - bx0 - 0.9) * p) / nx;
      colAt(x, bz1 - 0.45);
      if (L >= 2) colAt(x, bz0 + 0.45);
    }
    if (L === 3) for (let p = 1; p < nz; p++) { const z = bz0 + 0.45 + ((bz1 - bz0 - 0.9) * p) / nz; colAt(bx0 + 0.45, z); colAt(bx1 - 0.45, z); }
    s.grp(); const ci = L === 3 ? 2.2 : 1.1, cx0 = bx0 + ci, cx1 = bx1 - ci, cz0 = bz0 + (L >= 2 ? 2.4 : 0.2), cz1 = bz1 - 2.4;
    s.box(cx0, cy, cz0, cx1, top, cz1, marble);
    s.face(0, cz1, 0, -0.9, 0.9, cy, cy + 2.6, (i, j) => (j % 4 === 0 ? s.pal.trim : 0x3a2a1a));
    s.glow(1).face(0, cz1, 0, -0.25, 0.25, cy + 0.6, cy + 1.6, 0xffb340, -0.6 * s.vs, 0.4 * s.vs).glow(0);
    s.grp(); s.ring(bx0, bz0, bx1, bz1, top, top + 0.75, 0, 0.9, (i, j) => (j % 3 === 0 ? s.pal.trim : marble(i, j, 0)));
    s.box(bx0 + 0.9, top + 0.55, bz0 + 0.9, bx1 - 0.9, top + 0.75, bz1 - 0.9, marble);
    const rh = w * 0.22;
    s.roofGable(bx0, bx1, bz0, bz1, top + 0.75, rh, 'z', s.mat('roof', 'tile'), marble, 0.3);
    s.shape('gablez', 0, top + 0.75 + rh * 0.42, bz1 - 0.05, (bx1 - bx0) * 0.33, rh * 0.32, 0.1, T);
    if (L >= 2) for (const z of [bz0 - 0.3, bz1 + 0.3]) {
      s.box(-0.25, top + 0.75 + rh, z - 0.15, 0.25, top + 0.75 + rh + 0.7, z + 0.15, s.pal.trim, true);
      for (const sx of [-1, 1]) s.box(sx * (bx1 - 0.2) - 0.2, top + 0.75, z - 0.15, sx * (bx1 - 0.2) + 0.2, top + 1.3, z + 0.15, s.pal.trim, true);
    }
    if (L >= 2) for (const sx of [-1, 1]) {
      s.grp(); const bxp = sx * (w / 2 - 0.4), bzp = Math.min(hd - 0.5, z1 + 0.6);
      s.box(bxp - 0.25, 0, bzp - 0.25, bxp + 0.25, 1.2, bzp + 0.25, s.pal.trim === T ? GOLD : s.pal.trim);
      s.glow(1).shape('cone', bxp, 1.55, bzp, 0.3, 0.35, 0.3, 0xff9a30).glow(0);
    }
    s.spawn = [0, r3(hd + 1)];
  },
};

/* radiation trefoil sign on a +z face */
function trefoil(s, cx, cy, zf, r) {
  const vs = s.vs, [i0, i1] = s.rng(cx - r, cx + r), [j0, j1] = s.rng(cy - r, cy + r), k = Math.floor(zf / vs);
  for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) {
    const dx = (i + 0.5) * vs - cx, dy = (j + 0.5) * vs - cy, d = Math.hypot(dx, dy);
    if (d > r) continue;
    const a = (Math.atan2(dy, dx) * 180) / Math.PI + 360 + 30;
    const dark = d < r * 0.2 || (d > r * 0.3 && d < r * 0.88 && Math.floor(a / 60) % 2 === 0);
    s.put(i, j, k, dark ? DARK : s.pal.accent);
  }
}

/* futuristic laser pylon with a yawing head (ROLE.TURRET part) */
function pylon(s, r0) {
  const L = s.L, h = [5.5, 7.5, 9.5][L - 1], r = r0 * 0.62;
  s.grp(); s.col(-r, -r, r, r);
  s.cyl(0, 0, r, 0, 0.8, s.mat('wall')); s.glow(1).cyl(0, 0, r + s.vs, 0.5, 0.65, s.pal.accent).glow(0);
  s.cyl(0, 0, r * 0.45, 0.8, h, s.mat('wall'));
  s.glow(1); for (let y = 1.8; y < h - 0.6; y += [2.6, 2.0, 1.6][L - 1]) s.cyl(0, 0, r * 0.45 + s.vs, y, y + 0.2, s.pal.accent); s.glow(0);
  if (L >= 2) for (const [sx, sz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) s.box(sx * r * 0.45 - (sz ? 0.06 : 0) + (sx < 0 ? -r * 0.4 : 0), 0.8, sz * r * 0.45 - (sx ? 0.06 : 0) + (sz < 0 ? -r * 0.4 : 0), sx * r * 0.45 + (sz ? 0.06 : 0) + (sx > 0 ? r * 0.4 : 0), h * 0.55, sz * r * 0.45 + (sx ? 0.06 : 0) + (sz > 0 ? r * 0.4 : 0), s.pal.trim, true);
  if (L === 3) { s.glow(1).tube(0, 0, () => r * 0.95, h * 0.72, h * 0.72 + 0.2, s.vs * 1.5, s.pal.accent).glow(0); }
  // head
  s.grp(); s.turretHead(h);
  const hr = r * (L === 3 ? 0.9 : 0.75);
  s.shape('ellipsoid', 0, h + hr * 0.7, 0, hr, hr * 0.7, hr, (i, j) => (j * s.vs > h + hr * 0.7 - 0.12 && j * s.vs < h + hr * 0.7 + 0.12 ? T : s.pal.wall));
  const bar = L === 3 ? [-hr * 0.4, hr * 0.4] : [0];
  for (const bx of bar) {
    s.box(bx - 0.12, h + hr * 0.6, hr * 0.6, bx + 0.12, h + hr * 0.8, hr + 1.0, s.pal.trim, true);
    s.glow(1).box(bx - 0.12, h + hr * 0.6, hr + 1.0, bx + 0.12, h + hr * 0.8, hr + 1.2, s.pal.accent, true).glow(0);
  }
  s.glow(1).box(-hr * 0.5, h + hr * 0.95, hr * 0.7, hr * 0.5, h + hr * 1.1, hr * 0.95, s.pal.accent, true).glow(0);
  s.part = 0;
  s.turretFrom = [0, r3(h + hr * 0.7), r3(hr + 1.2)];
}

/* ---------------- recipe validation ---------------- */
const NAMED = { rot: '#c8322b', red: '#c8322b', blau: '#2a55a0', blue: '#2a55a0', gruen: '#4a8a3a', grün: '#4a8a3a', green: '#4a8a3a', gelb: '#f2c230', yellow: '#f2c230', weiss: '#f2f0ea', weiß: '#f2f0ea', white: '#f2f0ea', schwarz: '#1c1f24', black: '#1c1f24', grau: '#8a8f96', gray: '#8a8f96', grey: '#8a8f96', braun: '#8a5a33', brown: '#8a5a33', gold: '#d9b23c', silber: '#c6cdd5', silver: '#c6cdd5', orange: '#f08a2a', lila: '#7a4ab8', purple: '#7a4ab8', pink: '#f2a1b5', holz: '#8a5a33', stein: '#9a9a92' };
function normColor(v, allowKeys) {
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 0xffffff) return '#' + (v | 0).toString(16).padStart(6, '0');
  if (typeof v !== 'string' || v.length > 40) return null;
  const s = v.trim().toLowerCase();
  if (s === 'team' || s === 'team2') return 'team';
  if (allowKeys && PALETTE_KEYS.includes(s)) return s;
  let m = /^#?([0-9a-f]{6})$/.exec(s); if (m) return '#' + m[1];
  m = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(s); if (m) return '#' + m[1] + m[1] + m[2] + m[2] + m[3] + m[3];
  if (Object.prototype.hasOwnProperty.call(NAMED, s)) return NAMED[s];
  return null;
}
const TYPE_ALIAS = { burg: 'fortress', castle: 'fortress', festung: 'fortress', fort: 'fortress', turm: 'tower', mauer: 'wall', tor: 'gate', torhaus: 'gate', kaserne: 'barracks', haus: 'house', hof: 'farm', bauernhof: 'farm', werkstatt: 'workshop', reaktor: 'reactor', kuppel: 'dome', 'hütte': 'hut', huette: 'hut', tempel: 'temple', geschuetz: 'turret_base', turret: 'turret_base' };
const SHAPE_ALIAS = { sphere: 'ellipsoid', kugel: 'ellipsoid', cube: 'box', quader: 'box', cyl: 'cylinder', zylinder: 'cylinder', pyramid: 'cone', kegel: 'cone' };
function vec3(v, d, errs, path) {
  if (!Array.isArray(v)) { if (v !== undefined) errs.push(`${path}: expected [x,y,z]`); else errs.push(`${path}: missing`); return d.slice(); }
  const out = [0, 1, 2].map(i => toNum(get(v, i), NaN));
  if (v.length !== 3 || out.some(n => Number.isNaN(n))) errs.push(`${path}: repaired`);
  return out.map((n, i) => (Number.isNaN(n) ? d[i] : n));
}
function defaultRecipe(type = 'house', era = 'mittelalter') {
  return { name: TYPE_INFO[type].name, type, era, footprint: TYPE_INFO[type].fp.slice(), palette: { ...ERA_PALETTE[era] }, parts: [] };
}
/**
 * Validate + repair a building recipe. Never throws.
 * @returns {{ ok: boolean, recipe: object, errors: string[] }} recipe always compiles with compileBuilding.
 */
export function validateBuildingRecipe(input) {
  const errs = [];
  try {
    if (!isObj(input)) { errs.push('recipe: expected an object'); return { ok: false, recipe: defaultRecipe(), errors: errs }; }
    let type = get(input, 'type');
    if (typeof type === 'string') { const t = type.trim().toLowerCase(); type = BUILDING_TYPES.includes(t) ? t : TYPE_ALIAS[t]; if (type !== get(input, 'type')) errs.push('type: repaired'); }
    if (!BUILDING_TYPES.includes(type)) { errs.push('type: invalid'); type = 'house'; }
    let era = get(input, 'era');
    if (typeof era === 'string') { const e = era.trim().toLowerCase(); if (e !== era) errs.push('era: repaired'); era = e; }
    if (!BUILDING_ERAS.includes(era)) { errs.push('era: invalid'); era = 'mittelalter'; }
    let name = get(input, 'name');
    if (typeof name !== 'string' || !name.trim()) { errs.push('name: missing'); name = TYPE_INFO[type].name; }
    // eslint-disable-next-line no-control-regex
    else { const n = name.replace(/[\u0000-\u001f\u007f<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, LIMITS.name); if (n !== name) errs.push('name: repaired'); name = n || TYPE_INFO[type].name; }
    const fpIn = get(input, 'footprint'), fpd = TYPE_INFO[type].fp;
    const footprint = [0, 1].map(i => {
      const raw = Array.isArray(fpIn) ? toNum(get(fpIn, i), NaN) : NaN;
      if (Number.isNaN(raw)) { errs.push(`footprint[${i}]: missing`); return fpd[i]; }
      const v = Math.round(clamp(raw, LIMITS.footprint[0], LIMITS.footprint[1]) * 2) / 2;
      if (v !== raw) errs.push(`footprint[${i}]: clamped`);
      return v;
    });
    if (Array.isArray(fpIn) && fpIn.length !== 2) errs.push('footprint: expected [w,d]');
    const palIn = get(input, 'palette'), palette = {};
    if (!isObj(palIn)) errs.push('palette: missing');
    for (const k of PALETTE_KEYS) {
      const c = isObj(palIn) ? normColor(get(palIn, k), false) : null;
      if (c === null) { if (isObj(palIn)) errs.push(`palette.${k}: invalid`); palette[k] = ERA_PALETTE[era][k]; }
      else { if (c !== get(palIn, k)) errs.push(`palette.${k}: repaired`); palette[k] = c; }
    }
    const partsIn = get(input, 'parts'), parts = [];
    if (!Array.isArray(partsIn)) { if (partsIn !== undefined) errs.push('parts: expected an array'); else errs.push('parts: missing'); }
    else {
      const n = Math.min(partsIn.length, 400);
      if (partsIn.length > MAX_PARTS) errs.push(`parts: capped at ${MAX_PARTS}`);
      for (let i = 0; i < n && parts.length < MAX_PARTS; i++) {
        const p = get(partsIn, i), path = `parts[${i}]`;
        if (!isObj(p)) { errs.push(`${path}: dropped (not an object)`); continue; }
        let shape = get(p, 'shape');
        if (typeof shape === 'string') { const sl = shape.trim().toLowerCase(); shape = PART_SHAPES.includes(sl) ? sl : SHAPE_ALIAS[sl]; if (shape !== get(p, 'shape')) errs.push(`${path}.shape: repaired`); }
        if (!PART_SHAPES.includes(shape)) { errs.push(`${path}.shape: invalid -> box`); shape = 'box'; }
        const at = vec3(get(p, 'at'), [0, 1, 0], errs, `${path}.at`), size = vec3(get(p, 'size'), [0.2, 0.5, 0.2], errs, `${path}.size`);
        const rotIn = get(p, 'rot'), rot = rotIn === undefined ? (errs.push(`${path}.rot: missing`), [0, 0, 0]) : vec3(rotIn, [0, 0, 0], errs, `${path}.rot`);
        const lim = [
          [at, 0, -LIMITS.atXZ, LIMITS.atXZ], [at, 1, LIMITS.atY[0], LIMITS.atY[1]], [at, 2, -LIMITS.atXZ, LIMITS.atXZ],
          [size, 0, LIMITS.sizeXZ[0], LIMITS.sizeXZ[1]], [size, 1, LIMITS.sizeY[0], LIMITS.sizeY[1]], [size, 2, LIMITS.sizeXZ[0], LIMITS.sizeXZ[1]],
          [rot, 0, -LIMITS.rot, LIMITS.rot], [rot, 1, -LIMITS.rot, LIMITS.rot], [rot, 2, -LIMITS.rot, LIMITS.rot],
        ];
        let clamped = false;
        for (const [arr, k, a, b] of lim) { const v = r3(clamp(arr[k], a, b)); if (v !== arr[k]) clamped = true; arr[k] = v; }
        if (clamped) errs.push(`${path}: clamped`);
        let color = normColor(get(p, 'color'), true);
        if (color === null) { errs.push(`${path}.color: invalid -> accent`); color = 'accent'; } else if (color !== get(p, 'color')) errs.push(`${path}.color: repaired`);
        const mirrorIn = get(p, 'mirror'), glowIn = get(p, 'glow'), lvlIn = get(p, 'minLevel');
        if (typeof mirrorIn !== 'boolean') errs.push(`${path}.mirror: repaired`);
        if (typeof glowIn !== 'boolean') errs.push(`${path}.glow: repaired`);
        let minLevel = Math.round(toNum(lvlIn, 1));
        minLevel = clamp(minLevel, 1, 3);
        if (minLevel !== lvlIn) errs.push(`${path}.minLevel: repaired`);
        parts.push({ shape, at, size, color, rot, mirror: mirrorIn === true || mirrorIn === 'true', minLevel, glow: glowIn === true || glowIn === 'true' });
      }
    }
    for (const k of Object.keys(input)) if (!['name', 'type', 'era', 'footprint', 'palette', 'parts'].includes(k)) { errs.push(`${String(k).slice(0, 20)}: unknown key dropped`); break; }
    return { ok: errs.length === 0, recipe: { name, type, era, footprint, palette, parts }, errors: errs.slice(0, 50) };
  } catch (e) {
    errs.push('validator: ' + String((e && e.message) || e).slice(0, 80));
    return { ok: false, recipe: defaultRecipe(), errors: errs.slice(0, 50) };
  }
}

/* ---------------- compile ---------------- */
function partWork(R) {
  let vol = 0;
  for (const p of R.parts) {
    const hx = p.size[0] * R.footprint[0] / 2, hy = p.size[1], hz = p.size[2] * R.footprint[1] / 2;
    const rotd = p.rot[0] || p.rot[1] || p.rot[2];
    const e = rotd ? Math.hypot(hx, hy, hz) : 0;
    vol += (rotd ? 8 * e * e * e : 8 * hx * hy * hz) * (p.mirror ? 2 : 1);
  }
  return vol;
}
function addParts(s, R, level) {
  const hw = s.hw, hd = s.hd;
  for (const p of R.parts) {
    if (p.minLevel > level) continue;
    s.grp();
    const c = p.color === 'team' ? T : PALETTE_KEYS.includes(p.color) ? (p.color === 'wall' || p.color === 'roof' ? s.mat(p.color) : s.pal[p.color]) : hexInt(p.color);
    s.glow(p.glow);
    for (const mir of p.mirror ? [false, true] : [false]) {
      const cx = (mir ? -1 : 1) * p.at[0] * hw, cy = p.at[1], cz = p.at[2] * hd;
      s.shape(p.shape, cx, cy, cz, p.size[0] * hw, p.size[1], p.size[2] * hd, c, eulerInv(p.rot, mir));
    }
    s.glow(false);
  }
}
function trainsFor(R, L) {
  const tr = TYPE_INFO[R.type].trainer;
  return tr ? [...new Set(TRAINER[tr](ROSTER[R.era])[L - 1])] : [];
}
function turretFor(R, L, from) {
  const info = TYPE_INFO[R.type];
  if (!info.weapon) return undefined;
  const k = L - 1;
  if (info.weapon === 'silo') return { weapon: 'raketen', range: [120, 140, 160][k], dmg: [600, 800, 1000][k], cd: [30, 25, 20][k], aoe: [10, 12, 14][k], proj: 0, pspeed: 30, arc: true, hitscan: false, from: from || [0, 9, 0] };
  const key = (info.weapon === 'bunker' ? BUNKER_WEAPON : TOWER_WEAPON)[R.era], w = ALL_WEAPONS[key] || WEAPONS.gewehr;
  const mR = info.weapon === 'tower' ? 1.3 : 1.1;
  return {
    weapon: key, range: r3((w.range || 20) * mR * [1, 1.1, 1.2][k]), dmg: r3(w.dmg * [1, 1.3, 1.6][k]), cd: w.cd, aoe: w.aoe || 0,
    proj: w.hitscan ? -1 : (w.proj ?? 0), pspeed: w.pspeed || 0, arc: !!w.arc, hitscan: !!w.hitscan, from: from || [0, 3, 0],
  };
}
function buildingStats(R, L, s) {
  const info = TYPE_INFO[R.type], k = L - 1;
  const area = (R.footprint[0] * R.footprint[1]) / (info.fp[0] * info.fp[1]);
  const st = {
    hp: Math.round((info.hp * [1, 1.6, 2.4][k] * clamp(Math.sqrt(area), 0.5, 2)) / 10) * 10,
    armor: r3(Math.min(0.9, info.armor + 0.05 * k)),
    troopCap: info.cap ? info.cap[k] : 0,
    trains: trainsFor(R, L),
    cost: info.cost,
    upgradeCost: info.up.slice(),
    nextCost: L < MAX_LEVEL ? info.up[k] : null,
    income: info.income ? info.income[k] : 0,
  };
  const t = turretFor(R, L, s.turretFrom);
  if (t) st.turret = t;
  return st;
}
/**
 * Compile a building recipe at an upgrade level.
 * @param {object} recipe  any input (it is validated/repaired first)
 * @param {1|2|3} level
 * @param {{ vs?: number }} opt  voxel size in metres (default 0.25; increased automatically to stay under MAX_BUILDING_VOXELS)
 */
export function compileBuilding(recipe, level = 1, { vs } = {}) {
  const R = validateBuildingRecipe(recipe).recipe;
  const L = clamp(Math.round(toNum(level, 1)), 1, MAX_LEVEL);
  let v = clamp(toNum(vs, DEFAULT_VS), 0.1, 2);
  const work = partWork(R);
  if (work / (v * v * v) > WORK_BUDGET) v = Math.min(2, Math.cbrt(work / WORK_BUDGET));
  let s = null;
  for (let tries = 0; tries < 8; tries++) {
    s = new Site(R, L, v);
    TPL[R.type](s);
    addParts(s, R, L);
    if (s.m.map.size <= MAX_BUILDING_VOXELS || v >= 2) break;
    v = Math.min(2, v * 1.2);
  }
  let maxJ = 0; for (const k of s.m.map.keys()) { const j = ((k >> 10) & 1023) - 512; if (j > maxJ) maxJ = j; }
  if (s.m.map.size === 0) s.box(-0.5, 0, -0.5, 0.5, 0.5, 0.5, s.pal.wall === T ? WOOD : s.pal.wall);
  s.m.turret = s.m.parts.some(p => p.role === ROLE.TURRET);
  return {
    model: s.m,
    level: L,
    vs: s.vs,
    footprint: { w: R.footprint[0], d: R.footprint[1] },
    height: r3((maxJ + 1) * s.vs),
    slots: s.slots,
    colliders: s.colliders,
    spawn: s.spawn,
    stats: buildingStats(R, L, s),
    name: R.name, type: R.type, era: R.era,
  };
}

/* ---------------- meshing helper (interior culling + per-component outlines) ---------------- */
function interiorTest(m) {
  let mn = [1e9, 0, 1e9], mx = [-1e9, -1e9, -1e9];
  for (const k of m.map.keys()) {
    const x = (k & 1023) - 512, y = ((k >> 10) & 1023) - 512, z = ((k >> 20) & 1023) - 512;
    if (x < mn[0]) mn[0] = x; if (z < mn[2]) mn[2] = z; if (x > mx[0]) mx[0] = x; if (y > mx[1]) mx[1] = y; if (z > mx[2]) mx[2] = z;
  }
  if (mx[1] < 0) return () => false;
  const ox = mn[0] - 1, oz = mn[2] - 1, nx = mx[0] - mn[0] + 3, ny = mx[1] + 2, nz = mx[2] - mn[2] + 3;
  const N = nx * ny * nz; if (N > 8e6) return y => y < 0;
  const g = new Uint8Array(N); // 1 = voxel, 2 = outside air
  const id = (x, y, z) => (x - ox) + nx * (y + ny * (z - oz));
  for (const k of m.map.keys()) { const y = ((k >> 10) & 1023) - 512; if (y >= 0) g[id((k & 1023) - 512, y, ((k >> 20) & 1023) - 512)] = 1; }
  const q = new Int32Array(N); let qh = 0, qt = 0;
  const push = i => { if (g[i] === 0) { g[i] = 2; q[qt++] = i; } };
  for (let y = 0; y < ny; y++) for (let x = 0; x < nx; x++) { push(x + nx * y); push(x + nx * (y + ny * (nz - 1))); }
  for (let y = 0; y < ny; y++) for (let z = 0; z < nz; z++) { push(nx * (y + ny * z)); push(nx - 1 + nx * (y + ny * z)); }
  for (let x = 0; x < nx; x++) for (let z = 0; z < nz; z++) push(x + nx * (ny - 1 + ny * z));
  while (qh < qt) {
    const i = q[qh++], x = i % nx, r = (i - x) / nx, y = r % ny, z = (r - y) / ny;
    if (x > 0) push(i - 1); if (x < nx - 1) push(i + 1);
    if (y > 0) push(i - nx); if (y < ny - 1) push(i + nx);
    if (z > 0) push(i - nx * ny); if (z < nz - 1) push(i + nx * ny);
  }
  const test = (y, x, z) => {
    if (y < 0) return true;
    const a = x - ox, c = z - oz; if (a < 0 || a >= nx || y >= ny || c < 0 || c >= nz) return false;
    return g[a + nx * (y + ny * c)] === 0;
  };
  test.bounds = [mn[0], 0, mn[2], mx[0], mx[1], mx[2]];
  return test;
}
/**
 * Mesh a compiled building model for rendering (ctr = origin, metres). Faces facing enclosed interior air or the ground
 * are culled; the comic outline uses one hull centre per drawn component (better than one centre per building).
 * Returns a BufferGeometry with position, normal, color, aPart (real part ids), aEmit, aOut.
 */
export function meshBuilding(model, team = 0, { greedy = false, thick = 1.1, sat = 1.1 } = {}) {
  const solid = interiorTest(model), ctr = { cx: 0, cz: 0, y0: 0 };
  // outline groups: every drawn component gets its own hull centre; aPart is mapped back to the real part afterwards
  const gid = new Map(), gpart = [];
  const grpOf = v => { const key = (v.g || 0) * 64 + v.p; let g = gid.get(key); if (g === undefined) { g = gpart.length; gid.set(key, g); gpart.push(v.p); } return g; };
  let geo;
  if (greedy) {
    const fill = new Map();
    for (const [k, v] of model.map) fill.set(k, { c: v.c, p: grpOf(v), e: v.e });
    // fill all enclosed air so the greedy mesher emits no inner faces
    const b = solid.bounds;
    if (b) for (let z = b[2]; z <= b[5]; z++) for (let y = 0; y <= b[4]; y++) for (let x = b[0]; x <= b[3]; x++) {
      const n = KEY(x, y, z);
      if (!fill.has(n) && solid(y, x, z)) fill.set(n, { c: 0, p: 0, e: 0 });
    }
    geo = meshGreedy({ vs: model.vs, map: fill }, team, ctr, 1, sat);
  } else {
    const view = {
      vs: model.vs,
      map: {
        has: k => model.map.has(k) || solid(((k >> 10) & 1023) - 512, (k & 1023) - 512, ((k >> 20) & 1023) - 512),
        *[Symbol.iterator]() { for (const [k, v] of model.map) yield [k, { c: v.c, p: grpOf(v), e: v.e }]; },
      },
    };
    geo = meshAO(view, team, ctr, 0.05, false, sat);
  }
  addOutline(geo, model.vs, thick);
  const pa = geo.getAttribute('aPart');
  for (let i = 0; i < pa.count; i++) pa.array[i] = gpart[pa.array[i]] ?? 0;
  pa.needsUpdate = true;
  return geo;
}

/* ---------------- hardpoint turrets (placed on slots) ---------------- */
const tw = (vs) => new VoxModel(vs);
const disc = (m, r, y0, y1, c) => { for (let x = -r; x < r; x++) for (let z = -r; z < r; z++) if ((x + 0.5) ** 2 + (z + 0.5) ** 2 <= r * r) for (let y = y0; y <= y1; y++) m.set(x, y, z, typeof c === 'function' ? c(x, y, z) : c); };
export const TURRETS = {
  katapult: {
    name: 'Katapult', kinds: ['catapult'],
    stats: { weapon: 'katapult', range: 48, minRange: 8, dmg: 180, cd: 5, aoe: 4.5, proj: 1, pspeed: 18, arc: true, hitscan: false, hp: 400 },
    model() {
      const m = tw(0.1), head = m.addPart(ROLE.TURRET, 2, 0);
      m.use(0); disc(m, 8, 0, 1, (x, y, z) => ((x + z) & 1 ? WOOD_D : 0x4a3220));
      m.use(head).box(-7, 2, -9, -5, 3, 8, WOOD).box(4, 2, -9, 6, 3, 8, WOOD).box(-5, 2, -8, 4, 3, -7, WOOD).box(-5, 2, 6, 4, 3, 7, WOOD);
      for (const [x, z] of [[-8, -7], [7, -7], [-8, 5], [7, 5]]) m.box(x, 1, z, x, 5, z + 3, (X, y, Z) => (y === 3 && Z === z + 1 ? IRON : WOOD_D));
      m.box(-6, 4, -1, -5, 12, 0, WOOD).box(4, 4, -1, 5, 12, 0, WOOD).box(-6, 12, -1, 5, 13, 0, WOOD_L);
      for (let t = 0; t <= 12; t++) m.box(-1, 4 + Math.round((t * 10) / 12), -7 + t, 0, 5 + Math.round((t * 10) / 12), -7 + t, WOOD_L);
      m.box(-2, 14, 4, 1, 15, 7, WOOD_D).box(-1, 16, 5, 0, 16, 6, STONE).box(-3, 3, -9, 2, 5, -8, STONE_D);
      m.box(6, 13, 0, 6, 17, 0, WOOD_D).box(7, 15, 0, 9, 17, 0, T).set(7, 14, 0, T2);
      m.kind = 'turret'; m.turret = true; m.headPart = head; m.muzzle = [0, 16, 6];
      return m;
    },
  },
  balliste: {
    name: 'Balliste', kinds: ['catapult', 'archer'],
    stats: { weapon: 'balliste', range: 40, dmg: 90, cd: 3, aoe: 0, proj: 0, pspeed: 36, arc: false, hitscan: false, hp: 300 },
    model() {
      const m = tw(0.1), head = m.addPart(ROLE.TURRET, 6, 0);
      m.use(0).box(-1, 0, -1, 0, 5, 0, WOOD_D).box(-4, 0, -1, 3, 0, 0, WOOD_D).box(-1, 0, -4, 0, 0, 3, WOOD_D);
      m.use(head).box(-1, 6, -8, 0, 7, 8, WOOD).box(-2, 5, -9, 1, 6, -7, WOOD_D);
      for (let t = 0; t <= 8; t++) { const z = 6 - Math.round((t * t) / 12); m.box(1 + t, 7, z, 1 + t, 8, z, WOOD_L); m.box(-2 - t, 7, z, -2 - t, 8, z, WOOD_L); }
      m.box(-9, 8, 1, 8, 8, 1, 0xe8e2d2).box(-1, 9, -6, 0, 9, 10, WOOD_L).box(-1, 9, 11, 0, 9, 12, STEEL);
      m.box(-2, 8, 3, 1, 9, 3, IRON).box(-1, 10, -8, 0, 12, -8, T).box(-1, 11, -9, 0, 11, -9, T2);
      m.kind = 'turret'; m.turret = true; m.headPart = head; m.muzzle = [0, 9, 13];
      return m;
    },
  },
  kanone: {
    name: 'Kanone', kinds: ['turret'],
    stats: { weapon: 'kanone', range: 50, dmg: 140, cd: 3.5, aoe: 4, proj: 1, pspeed: 65, arc: false, hitscan: false, hp: 600 },
    model() {
      const m = tw(0.1), head = m.addPart(ROLE.TURRET, 2, 0);
      m.use(0); disc(m, 8, 0, 1, (x, y, z) => (hash3(x, y, z) > 0.5 ? SAND : 0xb8a06a));
      m.use(head).box(-4, 2, -6, 3, 4, 3, OLIVE).box(-6, 3, 3, 5, 9, 3, (x, y) => (y === 8 ? T : OLIVE_L)).box(-1, 6, 3, 0, 6, 3, DARK);
      m.box(-1, 5, -2, 0, 6, 16, DARK).box(-2, 4, 15, 1, 7, 16, DARK).box(-2, 3, -5, 1, 4, -3, OLIVE_D);
      m.box(-5, 2, -9, -4, 3, -5, OLIVE_D).box(3, 2, -9, 4, 3, -5, OLIVE_D);
      m.kind = 'turret'; m.turret = true; m.headPart = head; m.muzzle = [0, 6, 17];
      return m;
    },
  },
  mg: {
    name: 'Maschinengewehr', kinds: ['mg', 'turret'],
    stats: { weapon: 'mg', range: 30, dmg: 8, cd: 0.1, aoe: 0, proj: -1, pspeed: 0, arc: false, hitscan: true, hp: 250 },
    model() {
      const m = tw(0.08), head = m.addPart(ROLE.TURRET, 5, 0);
      m.use(0).box(-1, 0, -1, 0, 4, 0, DARK);
      for (const [dx, dz] of [[-1, -1], [1, -1], [0, 1]]) for (let t = 1; t <= 4; t++) m.set(dx * t - (dx < 0 ? 1 : 0), 4 - t, dz * t - (dz < 0 ? 1 : 0), DARK);
      m.use(head).box(-1, 5, -4, 0, 7, 4, DARK).box(-1, 6, 5, 0, 6, 12, (x, y, z) => (z & 1 ? 0x5b6066 : DARK)).box(-1, 5, 12, 0, 7, 13, DARK);
      m.box(1, 4, -2, 3, 6, 2, OLIVE).box(-5, 4, 3, 4, 9, 3, (x, y) => (y === 9 ? T : OLIVE_L)).box(-1, 6, 3, 0, 6, 3, DARK);
      m.box(-1, 5, -7, 0, 6, -5, 0x7a5532);
      m.kind = 'turret'; m.turret = true; m.headPart = head; m.muzzle = [0, 6, 14];
      return m;
    },
  },
  laser: {
    name: 'Lasergeschütz', kinds: ['laser', 'turret'],
    stats: { weapon: 'lasergewehr', range: 42, dmg: 30, cd: 0.5, aoe: 0, proj: -1, pspeed: 0, arc: false, hitscan: true, hp: 450 },
    model() {
      const m = tw(0.1), head = m.addPart(ROLE.TURRET, 3, 0);
      m.use(0); disc(m, 6, 0, 2, (x, y, z) => (y === 2 ? 0x5a616b : STEEL_L)); m.glow(1); disc(m, 4, 3, 3, CYAN); m.glow(0);
      m.use(head).sphere(0, 8, 0, 4, (x, y) => (y === 8 ? T : 0xe8ecf2));
      m.box(-3, 7, 3, -2, 8, 12, 0x5a616b).box(1, 7, 3, 2, 8, 12, 0x5a616b);
      m.glow(1).box(-3, 7, 13, -2, 8, 13, CYAN).box(1, 7, 13, 2, 8, 13, CYAN).box(-1, 10, 3, 0, 10, 4, CYAN).glow(0);
      m.kind = 'turret'; m.turret = true; m.headPart = head; m.muzzle = [0, 8, 14];
      return m;
    },
  },
};
/* which turret models fit a slot kind (first = default) */
export const SLOT_TURRETS = { catapult: ['katapult', 'balliste'], archer: ['balliste'], turret: ['kanone', 'mg'], mg: ['mg'], laser: ['laser'] };

/* ---------------- preset library ---------------- */
const part = (shape, at, size, color, o = {}) => ({ shape, at, size, color, rot: o.rot || [0, 0, 0], mirror: !!o.mirror, minLevel: o.minLevel || 1, glow: !!o.glow });
export const BUILDINGS = {
  huette: { name: 'Hütte', type: 'hut', era: 'urzeit', footprint: [8, 8], palette: { wall: '#8a5a33', roof: '#d6b25a', trim: '#5a3d22', accent: '#eadfc4' },
    parts: [
      part('cone', [0.06, 2.05, 0.38], [0.025, 0.5, 0.025], 'accent', { rot: [25, 0, -30], mirror: true }),
      part('ellipsoid', [-0.15, 0.08, 0.58], [0.12, 0.08, 0.06], '#b8865a'),
      part('box', [0.55, 0.5, 0.85], [0.03, 0.5, 0.03], 'trim', { minLevel: 2, mirror: true }),
      part('ellipsoid', [0.55, 1.05, 0.85], [0.06, 0.18, 0.06], 'accent', { minLevel: 2, mirror: true }),
    ] },
  palisade: { name: 'Palisade', type: 'palisade', era: 'urzeit', footprint: [12, 2], palette: { wall: '#8a5a33', roof: '#d6b25a', trim: '#6b4426', accent: '#eadfc4' },
    parts: [
      part('box', [0, 1.6, 0.62], [0.06, 0.55, 0.06], 'team'),
      part('ellipsoid', [0, 2.35, 0.65], [0.04, 0.22, 0.2], 'accent'),
    ] },
  steinturm: { name: 'Steinturm', type: 'tower', era: 'antike', footprint: [6, 6], palette: { wall: '#e6dcc2', roof: '#b8553a', trim: '#d9b23c', accent: '#2a4fb0' },
    parts: [
      part('box', [0, 3.0, 0], [0.57, 0.15, 0.57], 'trim'),
      part('box', [0, 3.0, 0], [0.69, 0.15, 0.69], 'trim', { minLevel: 2 }),
      part('box', [0, 3.0, 0], [0.8, 0.15, 0.8], 'trim', { minLevel: 3 }),
    ] },
  burgmauer: { name: 'Burgmauer', type: 'wall', era: 'mittelalter', footprint: [12, 3], palette: { wall: '#a3a39b', roof: '#5a6478', trim: '#6b4426', accent: '#d9b23c' },
    parts: [
      part('ellipsoid', [-0.55, 0.9, 0.62], [0.12, 0.5, 0.12], '#4a7a3a'),
      part('ellipsoid', [0.62, 1.4, 0.62], [0.08, 0.9, 0.1], '#5e8c3a', { minLevel: 2 }),
    ] },
  torhaus: { name: 'Torhaus', type: 'gate', era: 'mittelalter', footprint: [12, 6], palette: { wall: '#a3a39b', roof: '#5a6478', trim: '#6b4426', accent: '#d9b23c' },
    parts: [
      part('box', [0, 3.75, 0.48], [0.07, 0.25, 0.04], 'accent'),
      part('box', [0.17, 1.6, 0.48], [0.02, 0.06, 0.06], 'trim', { mirror: true, glow: false }),
      part('box', [0.63, 5.4, 0.62], [0.025, 0.14, 0.03], '#ffb340', { minLevel: 2, mirror: true, glow: true }),
    ] },
  festung: { name: 'Festung', type: 'fortress', era: 'mittelalter', footprint: [24, 24], palette: { wall: '#a3a39b', roof: '#4a5a78', trim: '#6b4426', accent: '#d9b23c' },
    parts: [
      part('box', [0, 2.2, 0.36], [0.03, 0.3, 0.02], 'accent'),
      part('ellipsoid', [-0.55, 0.5, -0.02], [0.08, 0.5, 0.08], '#5e8c3a', { minLevel: 2 }),
      part('box', [0.5, 0.35, -0.05], [0.06, 0.35, 0.06], '#a8743f', { minLevel: 2, mirror: true }),
      part('box', [0, 0.06, 0.66], [0.17, 0.06, 0.3], '#8a7a5a', { minLevel: 2 }),
    ] },
  kaserne: { name: 'Kaserne', type: 'barracks', era: 'weltkrieg', footprint: [16, 10], palette: { wall: '#a19f8c', roof: '#6b6f5a', trim: '#7a8070', accent: '#c8b878' },
    parts: [
      part('box', [-0.75, 0.35, 0.85], [0.06, 0.35, 0.08], '#6b4426'),
      part('box', [-0.6, 0.25, 0.86], [0.04, 0.25, 0.06], '#5f6b3a'),
      part('cylinder', [0.9, 0.5, -0.8], [0.04, 0.5, 0.06], '#3a3f47', { minLevel: 2 }),
    ] },
  bauernhaus: { name: 'Bauernhaus', type: 'house', era: 'mittelalter', footprint: [8, 8], palette: { wall: '#efe6d2', roof: '#b8553a', trim: '#5a3d22', accent: '#d8443c' },
    parts: [
      part('cylinder', [-0.78, 0.45, 0.62], [0.07, 0.45, 0.07], '#8a5a33'),
      part('box', [-0.78, 0.95, 0.62], [0.09, 0.06, 0.09], 'team'),
      part('ellipsoid', [0.82, 0.35, -0.75], [0.1, 0.35, 0.1], '#5e8c3a', { minLevel: 2 }),
    ] },
  bauernhof: { name: 'Bauernhof', type: 'farm', era: 'mittelalter', footprint: [18, 16], palette: { wall: '#a8443a', roof: '#6b4a3a', trim: '#5a3d22', accent: '#d9b23c' },
    parts: [
      part('cylinder', [-0.2, 0.4, 0.05], [0.05, 0.4, 0.05], '#7a5a3a'),
      part('ellipsoid', [-0.2, 0.15, 0.05], [0.03, 0.15, 0.03], '#f2f0ea'),
      part('box', [-0.05, 0.03, 0.55], [0.08, 0.03, 0.45], '#9a8460', { minLevel: 2 }),
    ] },
  werkstatt: { name: 'Werkstatt', type: 'workshop', era: 'mittelalter', footprint: [12, 10], palette: { wall: '#a3a39b', roof: '#8a4a3a', trim: '#5a3d22', accent: '#d9b23c' },
    parts: [
      part('cylinder', [0.6, 0.4, 0.75], [0.05, 0.4, 0.06], '#8a5a33'),
      part('box', [-0.14, 2.9, 0.09], [0.12, 0.25, 0.02], 'team'),
      part('cylinder', [-0.75, 0.6, 0.62], [0.08, 0.6, 0.1], '#6b4426', { minLevel: 3, rot: [0, 0, 90] }),
    ] },
  tempel: { name: 'Tempel', type: 'temple', era: 'antike', footprint: [12, 18], palette: { wall: '#ece4d0', roof: '#b8553a', trim: '#d9b23c', accent: '#2a4fb0' },
    parts: [
      part('cylinder', [0, 0.2, 0.97], [0.12, 0.2, 0.08], '#c8beaa', { minLevel: 2 }),
      part('ellipsoid', [0, 1.1, 0.97], [0.05, 0.7, 0.03], 'trim', { minLevel: 2 }),
    ] },
  bunker: { name: 'Bunker', type: 'bunker', era: 'weltkrieg', footprint: [8, 8], palette: { wall: '#9a9886', roof: '#6b6f5a', trim: '#5f6b3a', accent: '#c8b878' },
    parts: [
      part('box', [0, 0.9, 0.31], [0.22, 0.06, 0.02], '#d8d0b0'),
      part('box', [0, 0.9, 0.32], [0.04, 0.04, 0.02], 'team'),
    ] },
  sandsack: { name: 'Sandsackwall', type: 'palisade', era: 'weltkrieg', footprint: [10, 2], palette: { wall: '#a19f8c', roof: '#6b6f5a', trim: '#5f6b3a', accent: '#c8b878' },
    parts: [
      part('box', [-0.8, 0.2, -0.75], [0.06, 0.2, 0.2], '#5f6b3a'),
      part('box', [-0.66, 0.15, -0.75], [0.04, 0.15, 0.15], '#5f6b3a'),
      part('box', [0.1, 1.05, 0.2], [0.03, 0.04, 0.12], 'team', { minLevel: 2 }),
    ] },
  flakturm: { name: 'Flakturm', type: 'tower', era: 'weltkrieg', footprint: [8, 8], palette: { wall: '#9a9886', roof: '#6b6f5a', trim: '#5f6b3a', accent: '#c8b878' },
    parts: [
      part('box', [0.42, 0.45, 0.82], [0.14, 0.45, 0.1], '#c8b07a', { mirror: true }),
      part('cylinder', [-0.42, 1.2, 0.82], [0.05, 0.3, 0.05], '#3a3f47', { minLevel: 2 }),
      part('cylinder', [-0.42, 1.65, 0.86], [0.1, 0.15, 0.06], '#fff2b0', { minLevel: 2, rot: [70, 0, 0], glow: true }),
    ] },
  atomreaktor: { name: 'Atomreaktor', type: 'reactor', era: 'moderne', footprint: [22, 18], palette: { wall: '#c9cdd2', roof: '#5a616b', trim: '#3a3f47', accent: '#f2c230' },
    parts: [
      part('cylinder', [0.12, 0.45, 0.8], [0.03, 0.45, 0.04], 'accent', { mirror: true }),
      part('cylinder', [0, 0.45, 0.72], [0.03, 0.45, 0.04], 'accent'),
      part('ellipsoid', [0, 0.05, 0.86], [0.08, 0.05, 0.06], '#7cff4f', { glow: true }),
      part('box', [0.9, 2.2, 0.92], [0.01, 2.2, 0.012], '#3a3f47', { minLevel: 2 }),
      part('box', [0.95, 3.9, 0.92], [0.05, 0.3, 0.01], 'team', { minLevel: 2 }),
    ] },
  raketensilo: { name: 'Raketensilo', type: 'silo', era: 'moderne', footprint: [12, 12], palette: { wall: '#c9cdd2', roof: '#5a616b', trim: '#3a3f47', accent: '#f2c230' },
    parts: [
      part('box', [0.55, 3.2, 0.82], [0.24, 0.12, 0.01], 'team'),
      part('box', [-0.85, 0.4, 0.85], [0.04, 0.4, 0.04], '#f2c230', { glow: false }),
      part('ellipsoid', [-0.85, 0.85, 0.85], [0.04, 0.08, 0.04], '#ff3b3b', { glow: true }),
    ] },
  kaserne_modern: { name: 'Moderne Kaserne', type: 'barracks', era: 'moderne', footprint: [18, 12], palette: { wall: '#c9cdd2', roof: '#5a616b', trim: '#3a3f47', accent: '#f2c230' },
    parts: [
      part('box', [0.82, 0.4, 0.75], [0.1, 0.4, 0.12], '#4c5730'),
      part('box', [0.82, 0.85, 0.75], [0.11, 0.05, 0.13], '#3a3f47'),
      part('box', [-0.7, 0.3, 0.85], [0.08, 0.3, 0.07], '#f2c230', { minLevel: 2 }),
    ] },
  laserturm: { name: 'Laserturm', type: 'tower', era: 'zukunft', footprint: [5, 5], palette: { wall: '#e8ecf2', roof: '#3a3f5a', trim: '#5a616b', accent: '#3af0ff' },
    parts: [
      part('box', [0, 0.4, 0.62], [0.22, 0.05, 0.02], 'team'),
      part('cone', [0.62, 0.5, 0], [0.08, 0.5, 0.08], 'trim', { mirror: true, minLevel: 2 }),
    ] },
  energiekuppel: { name: 'Energiekuppel', type: 'dome', era: 'zukunft', footprint: [16, 16], palette: { wall: '#e8ecf2', roof: '#3a3f5a', trim: '#5a616b', accent: '#3af0ff' },
    parts: [
      part('box', [0, 0.65, 0], [0.62, 0.05, 0.62], '#5a616b', { minLevel: 1 }),
      part('box', [0, 0.05, 0.85], [0.12, 0.05, 0.15], 'team'),
      part('ellipsoid', [0.85, 0.3, 0.85], [0.05, 0.3, 0.05], 'accent', { glow: true, mirror: true, minLevel: 3 }),
    ] },
};
/* three diverse examples for the LLM system prompt (server/llm.js looks for BUILDING_EXAMPLES first) */
export const BUILDING_EXAMPLES = [BUILDINGS.festung, BUILDINGS.atomreaktor, BUILDINGS.bauernhaus];

export const BUILDING_PROMPT = `Grundform: "type" wählt eine prozedurale Vorlage (Mauern, Türme, Dächer, Türen entstehen automatisch und wachsen mit den Ausbaustufen 1-3).
"era" bestimmt Material und Stil (urzeit Holz/Stroh, antike Marmor/Ziegel, mittelalter Stein, weltkrieg Beton/Sandsäcke, moderne Beton/Metall, zukunft Metall mit Leuchtstreifen).
"footprint" [Breite, Tiefe] in Metern (2-32) ist das Baufeld der höchsten Ausbaustufe. Vorne ist +z (Tor, Tür).
"palette": wall, roof, trim, accent als "#rrggbb" oder "team". "parts" sind ZUSÄTZLICHE Formen über der Vorlage (später gewinnt):
at[0]/at[2] und size[0]/size[2] relativ zum Baufeld (-1 = linker/hinterer Rand, +1 = rechter/vorderer Rand), at[1] = Höhe der Mitte in Metern,
size[1] = halbe Höhe in Metern. size ist die HALBE Größe. color: "#rrggbb", "team" oder ein Palettenschlüssel (wall, roof, trim, accent).
rot = Drehung in Grad, mirror = zusätzliche Kopie bei -x, minLevel = ab welcher Ausbaustufe das Teil erscheint, glow = leuchtet.
Typisch 3-15 Teile für Signatur-Details (Wappen, Fahnen, Schilder, Antennen, Statuen, Bewuchs); keine Riesenformen, die die Vorlage verdecken.`;

/** Deterministic fallback recipe for a prompt (keyword match against the presets). */
export function fallbackBuildingRecipe(prompt = '') {
  const p = String(prompt || '').toLowerCase();
  let key = 'bauernhaus';
  for (const k of Object.keys(BUILDINGS)) if (p.includes(k.replace('_modern', '')) || p.includes(BUILDINGS[k].name.toLowerCase())) { key = k; break; }
  if (key === 'bauernhaus') for (const [alias, type] of Object.entries(TYPE_ALIAS)) if (p.includes(alias)) { key = Object.keys(BUILDINGS).find(k => BUILDINGS[k].type === type) || key; break; }
  const r = validateBuildingRecipe(BUILDINGS[key]).recipe;
  const name = String(prompt || '').replace(/\s+/g, ' ').trim().slice(0, LIMITS.name);
  if (name) r.name = name;
  return r;
}

/* ---------------- JSON schema (structured output subset) ---------------- */
const num = { type: 'number' };
const vec3s = d => ({ type: 'array', items: num, description: d });
const colorS = d => ({ type: 'string', description: d });
export const BUILDING_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['name', 'type', 'era', 'footprint', 'palette', 'parts'],
  properties: {
    name: { type: 'string', description: 'short display name (max 40 chars)' },
    type: { type: 'string', enum: BUILDING_TYPES.slice() },
    era: { type: 'string', enum: BUILDING_ERAS.slice() },
    footprint: { type: 'array', items: num, description: '[width, depth] in metres, 2..32; the plot at the highest upgrade level' },
    palette: {
      type: 'object', additionalProperties: false, required: PALETTE_KEYS.slice(),
      properties: Object.fromEntries(PALETTE_KEYS.map(k => [k, colorS("'#rrggbb' or 'team'")])),
    },
    parts: {
      type: 'array',
      description: `extra shapes layered over the procedural template, max ${MAX_PARTS}`,
      items: {
        type: 'object', additionalProperties: false,
        required: ['shape', 'at', 'size', 'color', 'rot', 'mirror', 'minLevel', 'glow'],
        properties: {
          shape: { type: 'string', enum: PART_SHAPES.slice() },
          at: vec3s('centre: x,z relative to the footprint (-1..1), y = height in metres'),
          size: vec3s('half size: x,z relative to the footprint half extents, y = half height in metres'),
          color: colorS("'#rrggbb', 'team' or a palette key: wall, roof, trim, accent"),
          rot: vec3s('rotation in degrees [x,y,z]'),
          mirror: { type: 'boolean' },
          minLevel: { type: 'integer', enum: [1, 2, 3] },
          glow: { type: 'boolean' },
        },
      },
    },
  },
};
